import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  File,
  Trash2,
  Download,
  Calendar,
  X,
  Trash,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Search,
  Filter,
  Eye,
  Edit,
  Settings,
  Clock,
  Play,
  Square,
  Info,
  Users,
  Folder,
  MoreVertical,
  FolderInput,
  Pencil,
  Check,
  Shield,
} from "lucide-react";
import JSZip from "jszip";
import { fileApi, folderApi } from "../services/api";
import MoveToFolderModal from "./MoveToFolderModal";
import FileViewer from "./FileViewer";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useUpload } from "../contexts/UploadContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { useModal } from "../hooks/useModal";
import { truncateFileName } from "../utils/textUtils";
import { moveFilesToFolder, moveFoldersToFolder } from "../utils/moveItems";
import CustomSelect from "./CustomSelect";
import SourceLinksField, {
  MAX_SOURCE_LINKS,
  normalizeLinks,
  toEntries,
  validateLinks,
} from "./SourceLinksField";
import FileSourceLinks from "./FileSourceLinks";

// Menus are `position: fixed` and previously anchored purely via
// `top: rect.bottom + 4`, which runs off the bottom of the viewport for
// rows near the end of the list. Flipping to a `bottom`-anchored position
// lets the browser size the menu upward from a fixed point regardless of
// its actual rendered height. The open direction and `maxHeight` clamp are
// decided from the menu's *measured* height (see the layout effect below),
// not a hardcoded guess — the item list varies by `locationWritable`,
// `file.file_type`, `isDateEnabledCollection`, `availableChats`,
// `isInternalProceduresManager()`, `folder.parent_folder_id`, and label
// length varies by locale, so any fixed estimate drifts from reality:
// either flipping upward when it doesn't need to, or not flipping enough
// and clipping items again.
// Uma campanha sincronizada da VTEX não se apaga como o ficheiro de uma pessoa: o
// gesto é uma rejeição para uma loja, o ficheiro só sai se mais nenhuma o publicar, e
// a decisão desfaz-se no painel de sincronização. O `created_by` é o que a listagem já
// traz para as distinguir, e é o mesmo `SYNC_AUTHOR` que o backend escreve.
const isSyncedCampaign = (file) => (file?.metadata?.created_by || file?.created_by) === "vtex-sync";

const MENU_GAP = 4;
const MENU_VIEWPORT_MARGIN = 8;

function computeMenuRight(rect) {
  return Math.max(MENU_VIEWPORT_MARGIN, window.innerWidth - rect.right);
}

// Placeholder position for the menu's first, invisible render — used only
// so it can mount and be measured (see the layout effect below). Anchored
// downward with no height clamp so it lays out at its natural height.
function computeMenuPlaceholder(rect) {
  return { top: rect.bottom + MENU_GAP, right: computeMenuRight(rect), visibility: "hidden" };
}

// The menu can also be opened by right-click, where there is no trigger element to
// anchor to — only a cursor position. Placement reads its anchor purely through
// `getBoundingClientRect()`, so a zero-size rect at the cursor is a complete anchor.
function cursorAnchor(clientX, clientY) {
  const rect = {
    top: clientY, bottom: clientY, y: clientY,
    left: clientX, right: clientX, x: clientX,
    width: 0, height: 0,
  };
  return { getBoundingClientRect: () => rect };
}

// `measuredHeight` is the menu's real rendered height. `maxHeight` is
// clamped to the space actually available on the side the menu opens
// towards, never forced above it: in a very short viewport the menu
// shrinks and its content scrolls, but it can never overflow past the
// viewport edge, which is the bug this exists to fix.
function computeActionMenuPosition(rect, measuredHeight, measuredWidth = 0) {
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openUpward = spaceBelow < measuredHeight && spaceAbove > spaceBelow;
  // Clamp `right` so a cursor anchor near the left edge can't push the menu's left
  // edge off screen; the trigger-button path is unaffected since it always anchors
  // at the row's right edge.
  const maxRight = Math.max(MENU_VIEWPORT_MARGIN, window.innerWidth - measuredWidth - MENU_VIEWPORT_MARGIN);
  const right = Math.min(computeMenuRight(rect), maxRight);
  // Clamp to the side actually opened towards, not the roomier side.
  const spaceUsed = openUpward ? spaceAbove : spaceBelow;
  const maxHeight = Math.max(0, spaceUsed - MENU_GAP - MENU_VIEWPORT_MARGIN);
  // `overflow: "hidden auto"` (not just `overflowY: "auto"`) also pins
  // overflow-x to hidden: left unset, it computes to `auto` per spec
  // whenever overflow-y isn't `visible`, which can grow an unwanted
  // horizontal scrollbar. `overscrollBehavior: "contain"` stops scrolling
  // the menu to its end from chaining to the page scroll, which the
  // capture-phase close-on-scroll handler below would otherwise read as a
  // page scroll and close the menu — in exactly the clamped, scrollable
  // case this positioning exists to protect.
  const scroll = { overflow: "hidden auto", overscrollBehavior: "contain" };

  return openUpward
    ? { bottom: Math.max(MENU_VIEWPORT_MARGIN, window.innerHeight - rect.top + MENU_GAP), right, maxHeight, ...scroll }
    : { top: rect.bottom + MENU_GAP, right, maxHeight, ...scroll };
}

const FileList = ({
  files,
  totalFiles = 0,
  isLoading = false,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  onFetchFiles,
  onFileDeleted,
  onRefresh,
  onFileUpdated,
  collectionName,
  availableChats = [], // Chats passed from FileManagement
  folderId = null,
  locationWritable = true,
  onNavigateToFolder,
  createFolderTrigger = 0,
  refreshFoldersTrigger = 0,
  nameFilter = "",
  setNameFilter,
  onSavingFolderChange,
  queuePanels = null,
  isDraggingItem = false,
  onDraggingChange,
  onDraggingOverFolderChange,
  onUploadToFolder,
  movingFilePaths,
  setMovingFilePaths,
  movingFolderIds,
  setMovingFolderIds,
  onOptimisticRemoveFiles,
  onOptimisticRemoveFolders,
  removeFolderIds = [],
}) => {
  const { translate } = useLanguage();
  const { isInternalProceduresManager } = useAuth();

  // Advisory mirror (spec §3): whether the user may write in the current
  // location, resolved by FileManagement from the current folder's department
  // (backend is the enforcement boundary; this only hides 403 dead-ends). At
  // the internal_procedures root / open subtrees this is false for non-managers,
  // so top-level folders and root files show read-only menus.

  const { initDeleteQueue, updateFileInDeleteQueue } = useUpload();
  const [deletingFile, setDeletingFile] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(new Set());

  // Prune stale selections when files change (e.g. after move, delete, filter)
  useEffect(() => {
    setSelectedFiles((prev) => {
      if (prev.size === 0) return prev;
      const currentPaths = new Set(files.map((f) => f.path));
      const pruned = new Set([...prev].filter((p) => currentPaths.has(p)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [files]);

  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [showBulkFolderDeleteConfirm, setShowBulkFolderDeleteConfirm] = useState(false);
  const [bulkFolderDeleting, setBulkFolderDeleting] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [bulkFolderDeleteCounts, setBulkFolderDeleteCounts] = useState(null);

  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showSingleDeleteConfirm, setShowSingleDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [sortBy, setSortBy] = useState("last_updated_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Campaign dates editing state
  const [showDateModal, setShowDateModal] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [modalStartDate, setModalStartDate] = useState(null);
  const [modalEndDate, setModalEndDate] = useState(null);
  const [modalSourceUrls, setModalSourceUrls] = useState([]);
  const [savingDates, setSavingDates] = useState(false);
  // Track if user has manually set a custom time (not the default 23:59)
  const hasCustomTimeRef = useRef(false);
  // Track the most recent modalEndDate value to avoid stale state issues
  const modalEndDateRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    modalEndDateRef.current = modalEndDate;
  }, [modalEndDate]);

  // Reset custom time flag when modal closes
  useEffect(() => {
    if (!showDateModal) {
      hasCustomTimeRef.current = false;
      modalEndDateRef.current = null;
    }
  }, [showDateModal]);

  // Chat management state
  const [showChatModal, setShowChatModal] = useState(false);
  const [editingFileChats, setEditingFileChats] = useState(null);
  const [selectedFileChats, setSelectedFileChats] = useState([]);
  const [savingChats, setSavingChats] = useState(false);

  const isDateEnabledCollection =
    collectionName === "campaigns" ||
    collectionName === "general_information" ||
    collectionName === "internal_procedures";

  // Helper function to convert Unix timestamp to Date
  const timestampToDate = useCallback((timestamp) => {
    if (!timestamp) return null;
    // If timestamp is a number (Unix timestamp), multiply by 1000 for JS Date
    if (typeof timestamp === "number") {
      return new Date(timestamp * 1000);
    }
    // If it's already a string (ISO format), use it directly
    return new Date(timestamp);
  }, []);

  const getCampaignStatus = useCallback(
    (metadata) => {
      if (!metadata) return null;

      const now = new Date();
      const startDate = timestampToDate(metadata.ai_agent_knowledge_start_date);
      const endDate = timestampToDate(metadata.ai_agent_knowledge_end_date);

      if (startDate && now < startDate) {
        return "pending";
      }
      if (endDate && now > endDate) {
        return "expired";
      }
      return "active";
    },
    [timestampToDate]
  );

  // Filter states
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [createdAtFilter, setCreatedAtFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [lastUpdatedByFilter, setLastUpdatedByFilter] = useState("");
  const [lastUpdatedAtFilter, setLastUpdatedAtFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [campaignStartDateFilter, setCampaignStartDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [campaignEndDateFilter, setCampaignEndDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [campaignStatusFilter, setCampaignStatusFilter] = useState("all");
  const [chatFilter, setChatFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [showSorting, setShowSorting] = useState(false);
  const PAGE_SIZE = 10;
  const sentinelRef = useRef(null);
  const debounceRef = useRef(null);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [startInEditMode, setStartInEditMode] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Folder state
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [foldersLoadingMore, setFoldersLoadingMore] = useState(false);
  const [hasMoreFolders, setHasMoreFolders] = useState(false);
  const [foldersCursor, setFoldersCursor] = useState(null);
  const [totalFolders, setTotalFolders] = useState(0);
  const FOLDER_PAGE_SIZE = 10;

  // Prune stale folder selections
  useEffect(() => {
    setSelectedFolders((prev) => {
      if (prev.size === 0) return prev;
      const currentIds = new Set(folders.map((f) => f.id));
      const pruned = new Set([...prev].filter((id) => currentIds.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [folders]);

  // Clear folder moving states when folders list is refreshed
  useEffect(() => {
    setMovingFolderIds(new Set());
  }, [folders, setMovingFolderIds]);

  // Cache of folder breadcrumb paths for search results: { folderId: "Parent / Child" }
  const [folderPathCache, setFolderPathCache] = useState({});
  const folderSentinelRef = useRef(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [folderMenuOpen, setFolderMenuOpen] = useState(null);
  const [fileMenuOpen, setFileMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  // Bumped on every right-click open so the effects below re-run even when the
  // open-state value is unchanged — on WebKit `contextmenu` can precede `mousedown`.
  const [menuAnchorNonce, setMenuAnchorNonce] = useState(0);

  // Close menus on click outside, scroll, resize, or Escape.
  const menuRef = useRef(null);
  // The trigger button of whichever menu is currently open — used to
  // measure/re-measure its position (layout effect below) and to restore
  // focus to it when the menu is dismissed via Escape.
  const triggerRef = useRef(null);
  useEffect(() => {
    if (!fileMenuOpen && !folderMenuOpen) return;
    const close = (e) => {
      // Don't close if clicking inside the menu
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setFileMenuOpen(null);
      setFolderMenuOpen(null);
    };
    // The scroll listener is registered in the capture phase, so it also sees
    // scrolling *inside* the menu — which is height-clamped and internally
    // scrollable. Only page/container scrolling moves the trigger button and
    // invalidates the menu's fixed position, so ignore in-menu scrolling.
    const closeOnScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setFileMenuOpen(null);
      setFolderMenuOpen(null);
    };
    const closeOnResize = () => { setFileMenuOpen(null); setFolderMenuOpen(null); };
    const closeOnEscape = (e) => {
      if (e.key !== "Escape") return;
      setFileMenuOpen(null);
      setFolderMenuOpen(null);
      // `triggerRef` holds the three-dot button when opened by click, or a
      // `cursorAnchor` object when opened by right-click — the latter has no
      // element to focus, so the call is optional.
      triggerRef.current?.focus?.();
    };
    document.addEventListener("keydown", closeOnEscape);
    // Use setTimeout to avoid closing immediately on the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", close);
      document.addEventListener("scroll", closeOnScroll, true);
      window.addEventListener("resize", closeOnResize);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [fileMenuOpen, folderMenuOpen, menuAnchorNonce]);

  // Menu height varies with which items render (see the comment above
  // computeActionMenuPosition), so the open direction and height clamp are
  // computed from the menu's real rendered height, not a guess. The click
  // handlers mount the menu first at a throwaway position
  // (computeMenuPlaceholder: `visibility: hidden`, no height clamp) so it
  // lays out at its natural height; this effect then measures that height
  // and commits the final position. Because this is a layout effect
  // (synchronous, before the browser paints), the hidden intermediate frame
  // is never actually painted — the user only ever sees the final,
  // correctly positioned menu.
  useLayoutEffect(() => {
    if (!fileMenuOpen && !folderMenuOpen) return;
    if (!menuRef.current || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    setMenuPosition(computeActionMenuPosition(rect, menuRect.height, menuRect.width));
  }, [fileMenuOpen, folderMenuOpen, menuAnchorNonce]);
  const [showFolderDeleteConfirm, setShowFolderDeleteConfirm] = useState(null);
  const [folderDeleteCounts, setFolderDeleteCounts] = useState(null);
  const [loadingDeleteFolderId, setLoadingDeleteFolderId] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(null); // { type: 'file'|'files'|'folder'|'folders', id|ids, name }
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(null); // folder object
  const [assignRoleValue, setAssignRoleValue] = useState("");
  const [savingRole, setSavingRole] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [draggingFolderIds, setDraggingFolderIds] = useState(new Set());

  // Notify parent when dragging over a folder (to suppress upload overlay)
  useEffect(() => {
    if (onDraggingOverFolderChange) onDraggingOverFolderChange(!!dragOverFolderId);
  }, [dragOverFolderId, onDraggingOverFolderChange]);

  const newFolderInputRef = useRef(null);
  const renameFolderInputRef = useRef(null);

  // Fetch folders for current location (initial or append via cursor)
  const fetchFolders = useCallback(async (cursor = null) => {
    if (cursor) {
      setFoldersLoadingMore(true);
    } else {
      setFoldersLoading(true);
    }
    try {
      const result = await folderApi.listFolders(collectionName, folderId, {
        sortBy: sortBy === "name" ? "name" : sortBy === "created_at" ? "created_at" : "last_updated_at",
        sortOrder,
        limit: FOLDER_PAGE_SIZE,
        cursor,
      });
      const newFolders = result.folders || [];
      if (cursor) {
        setFolders((prev) => [...prev, ...newFolders]);
      } else {
        setFolders(newFolders);
      }
      setFoldersCursor(result.next_cursor || null);
      setHasMoreFolders(!!result.next_cursor);
      if (result.total_count !== undefined) setTotalFolders(result.total_count);
    } catch (err) {
      console.error("Error fetching folders:", err);
    } finally {
      setFoldersLoading(false);
      setFoldersLoadingMore(false);
    }
  }, [collectionName, folderId, sortBy, sortOrder]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Optimistic folder removal — instantly remove from local state
  useEffect(() => {
    if (removeFolderIds.length > 0) {
      setFolders((prev) => prev.filter((f) => !removeFolderIds.includes(f.id)));
      setTotalFolders((prev) => Math.max(0, prev - removeFolderIds.length));
    }
  }, [removeFolderIds]);

  // Re-fetch folders when parent signals (e.g. after folder upload)
  useEffect(() => {
    if (refreshFoldersTrigger > 0) fetchFolders();
  }, [refreshFoldersTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll for folders
  const fetchMoreFolders = useCallback(() => {
    if (!foldersCursor || foldersLoadingMore) return;
    fetchFolders(foldersCursor);
  }, [foldersCursor, foldersLoadingMore, fetchFolders]);

  useEffect(() => {
    const sentinel = folderSentinelRef.current;
    if (!sentinel || !hasMoreFolders || !fetchMoreFolders) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreFolders && !foldersLoadingMore) {
          fetchMoreFolders();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreFolders, foldersLoadingMore, fetchMoreFolders]);

  const [savingFolder, setSavingFolder] = useState(false);

  // Notify parent of savingFolder state changes
  useEffect(() => {
    if (onSavingFolderChange) onSavingFolderChange(savingFolder);
  }, [savingFolder, onSavingFolderChange]);

  // Trigger folder creation from parent — ignore the initial value on mount
  const prevCreateTriggerRef = useRef(createFolderTrigger);
  useEffect(() => {
    if (createFolderTrigger !== prevCreateTriggerRef.current) {
      prevCreateTriggerRef.current = createFolderTrigger;
      // Ignore if currently saving a folder
      if (savingFolder) return;
      // If already creating and has a name, submit the current one first
      if (creatingFolder && newFolderName.trim()) {
        handleCreateFolder();
      }
      setCreatingFolder(true);
      setNewFolderName("");
      setTimeout(() => newFolderInputRef.current?.focus(), 50);
    }
  }, [createFolderTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect if any filter is active (search mode — searches across all folders)
  const hasActiveFilter = nameFilter ||
    fileTypeFilter !== "all" ||
    createdByFilter ||
    lastUpdatedByFilter ||
    createdAtFilter.startDate || createdAtFilter.endDate ||
    lastUpdatedAtFilter.startDate || lastUpdatedAtFilter.endDate ||
    campaignStartDateFilter.startDate || campaignStartDateFilter.endDate ||
    campaignEndDateFilter.startDate || campaignEndDateFilter.endDate ||
    campaignStatusFilter !== "all" ||
    chatFilter !== "all";

  // Fetch folder breadcrumb paths for search results
  const folderPathCacheRef = useRef(folderPathCache);
  folderPathCacheRef.current = folderPathCache;
  useEffect(() => {
    if (!hasActiveFilter) return;
    const uniqueFolderIds = [...new Set(
      files.filter((f) => f.folder_id).map((f) => f.folder_id)
    )].filter((id) => !folderPathCacheRef.current[id]);
    if (uniqueFolderIds.length === 0) return;

    Promise.all(
      uniqueFolderIds.map((id) =>
        folderApi.getFolderBreadcrumbs(id).then((res) => ({
          id,
          path: (res.breadcrumbs || []).map((b) => b.name).join(" / "),
        })).catch(() => ({ id, path: "—" }))
      )
    ).then((results) => {
      setFolderPathCache((prev) => {
        const next = { ...prev };
        results.forEach((r) => { next[r.id] = r.path; });
        return next;
      });
    });
  }, [files, hasActiveFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      setCreatingFolder(false);
      setNewFolderName("");
      return;
    }
    setSavingFolder(true);
    try {
      await folderApi.createFolder(collectionName, name, folderId);
      // Wait for folders to refresh before hiding the inline row
      await fetchFolders();
      setCreatingFolder(false);
      setNewFolderName("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || translate("failedToCreateFolder"));
    } finally {
      setSavingFolder(false);
    }
  };

  const [savingFolderIds, setSavingFolderIds] = useState(new Set());

  const handleRenameFolder = async (id) => {
    if (!renameFolderName.trim()) {
      setRenamingFolderId(null);
      return;
    }
    setRenamingFolderId(null);
    setSavingFolderIds((prev) => new Set([...prev, id]));
    try {
      await folderApi.renameFolder(id, renameFolderName.trim());
      setRenameFolderName("");
      await fetchFolders();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || translate("failedToRenameFolder"));
    } finally {
      setSavingFolderIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleDeleteFolder = async (id) => {
    setSavingFolderIds((prev) => new Set([...prev, id]));
    setShowFolderDeleteConfirm(null);
    setFolderDeleteCounts(null);
    try {
      await folderApi.deleteFolder(id);
      setFolders((prev) => prev.filter((f) => f.id !== id));
      setTotalFolders((prev) => Math.max(0, prev - 1));
      if (onOptimisticRemoveFolders) onOptimisticRemoveFolders([id]);
      toast.success(translate("deletedFolderSuccessfully"));
    } catch (err) {
      toast.error(err.response?.data?.detail || translate("failedToDeleteFolder"));
    } finally {
      setSavingFolderIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const handleFolderDeleteClick = async (folder) => {
    setFolderMenuOpen(null);
    setLoadingDeleteFolderId(folder.id);
    try {
      const counts = await folderApi.getFolderContentsCount(folder.id);
      setFolderDeleteCounts(counts);
      setShowFolderDeleteConfirm(folder);
    } catch {
      setFolderDeleteCounts({ folders_count: 0, files_count: 0 });
      setShowFolderDeleteConfirm(folder);
    } finally {
      setLoadingDeleteFolderId(null);
    }
  };

  const handleAssignRole = async () => {
    if (!showAssignRoleModal) return;
    // Mirror backend case normalization; empty clears the binding.
    const value = assignRoleValue.trim().toLowerCase();
    setSavingRole(true);
    try {
      await folderApi.setFolderRole(showAssignRoleModal.id, value || null);
      setShowAssignRoleModal(null);
      setAssignRoleValue("");
      toast.success(translate("roleAssigned"));
      await fetchFolders();
    } catch (err) {
      // Backend rejects reserved keys (the set STRUCTURAL_ROLES in
      // utils/roles.js mirrors) with a detail message; surface it.
      toast.error(err.response?.data?.detail || translate("failedToAssignRole"));
    } finally {
      setSavingRole(false);
    }
  };

  // The shared helpers own the outcome rules; these wrappers own the state only
  // this component has — the selection, and the local folder list.
  const moveFilePaths = async (paths, targetFolderId) => {
    const previousSelection = new Set(selectedFiles);
    setSelectedFiles(new Set());
    const { ok } = await moveFilesToFolder(paths, targetFolderId, {
      collectionName,
      translate,
      setMovingFilePaths,
      onOptimisticRemoveFiles,
    });
    // Hand the selection back so a failed batch can be retried or narrowed.
    if (!ok) setSelectedFiles(previousSelection);
  };

  const pruneMovedFolders = (moved) => {
    setFolders((prev) => prev.filter((f) => !moved.includes(f.id)));
    setTotalFolders((prev) => Math.max(0, prev - moved.length));
  };

  const folderMoveOptions = {
    translate,
    setMovingFolderIds,
    onOptimisticRemoveFolders,
    onFoldersMoved: pruneMovedFolders,
  };

  const moveFolderIds = async (ids, targetFolderId) => {
    if (ids.length === 0) return;
    setSelectedFolders(new Set());
    const { failed } = await moveFoldersToFolder(ids, targetFolderId, folderMoveOptions);
    // Per-folder requests tell us exactly which ids failed, so re-select just
    // those rather than the whole batch the way the file path has to.
    if (failed.length > 0) setSelectedFolders(new Set(failed));
  };

  const handleMoveToFolder = async (targetFolderId) => {
    if (!showMoveModal) return;
    const modal = showMoveModal;
    setShowMoveModal(null);
    try {
      if (modal.type === "files") {
        await moveFilePaths(modal.ids, targetFolderId);
      } else if (modal.type === "file") {
        await moveFilePaths([modal.id], targetFolderId);
      } else if (modal.type === "folders") {
        await moveFolderIds(modal.ids, targetFolderId);
      } else {
        await moveFoldersToFolder([modal.id], targetFolderId, folderMoveOptions);
      }
    } catch (err) {
      setMovingFilePaths(new Set());
      setMovingFolderIds(new Set());
      toast.error(err.response?.data?.detail || translate("failedToMove"));
    }
  };

  // Drag-and-drop handlers for moving items into folders
  const createDragGhost = (text) => {
    const ghost = document.createElement("div");
    ghost.textContent = text;
    ghost.style.cssText = "position:fixed;top:-1000px;left:-1000px;padding:8px 16px;border-radius:10px;background:#3ecf8e;color:#fff;font-size:13px;font-weight:600;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,0.15);display:flex;align-items:center;gap:6px;z-index:99999;";
    document.body.appendChild(ghost);
    return ghost;
  };

  const handleDragStart = (e, type, id) => {
    let ghost;
    if (type === "file" && selectedFiles.size > 1 && selectedFiles.has(id)) {
      const ids = Array.from(selectedFiles);
      e.dataTransfer.setData("application/json", JSON.stringify({ type: "files", ids }));
      ghost = createDragGhost(`${translate("move")} ${ids.length} ${ids.length === 1 ? translate("fileSingular") : translate("filePlural")}`);
    } else if (type === "file") {
      e.dataTransfer.setData("application/json", JSON.stringify({ type, id }));
      const file = files.find((f) => f.path === id);
      ghost = createDragGhost(`${translate("move")} ${file?.name || "file"}`);
    } else if (type === "folder" && selectedFolders.size > 1 && selectedFolders.has(id)) {
      const ids = Array.from(selectedFolders);
      e.dataTransfer.setData("application/json", JSON.stringify({ type: "folders", ids }));
      ghost = createDragGhost(`${translate("move")} ${ids.length} ${ids.length === 1 ? translate("folderSingular") : translate("folderPlural")}`);
    } else {
      e.dataTransfer.setData("application/json", JSON.stringify({ type, id }));
      const folder = folders.find((f) => f.id === id);
      ghost = createDragGhost(`${translate("move")} ${folder?.name || "folder"}`);
    }
    e.dataTransfer.setDragImage(ghost, 20, 20);
    e.dataTransfer.effectAllowed = "move";
    if (type === "folder" && selectedFolders.size > 1 && selectedFolders.has(id)) {
      setDraggingFolderIds(new Set(selectedFolders));
    } else if (type === "folder") {
      setDraggingFolderIds(new Set([id]));
    }
    if (onDraggingChange) onDraggingChange(true);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleDragEnd = () => {
    if (onDraggingChange) onDraggingChange(false);
    setDragOverFolderId(null);
    setDraggingFolderIds(new Set());
  };

  const handleFolderDrop = async (e, targetFolderId) => {
    e.preventDefault();
    setDragOverFolderId(null);

    // Check if this is an OS file drop (upload to folder)
    const osFiles = Array.from(e.dataTransfer.files);
    if (osFiles.length > 0 && !e.dataTransfer.getData("application/json")) {
      e.stopPropagation(); // Prevent container's onDrop from also handling this
      if (onUploadToFolder) onUploadToFolder(osFiles, targetFolderId, e.dataTransfer.items);
      return;
    }

    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      if (data.type === "files") {
        await moveFilePaths(data.ids, targetFolderId);
      } else if (data.type === "file") {
        await moveFilePaths([data.id], targetFolderId);
      } else if (data.type === "folders") {
        await moveFolderIds(data.ids.filter((id) => id !== targetFolderId), targetFolderId);
      } else if (data.type === "folder" && data.id !== targetFolderId) {
        await moveFoldersToFolder([data.id], targetFolderId, folderMoveOptions);
      }
    } catch (err) {
      setMovingFilePaths(new Set());
      setMovingFolderIds(new Set());
      toast.error(err.response?.data?.detail || translate("failedToMove"));
    }
  };

  const getFileIcon = (fileType) => {
    const colors = {
      pdf: "var(--color-error)",
      markdown: "var(--color-info)",
      text: "var(--color-mint)",
    };
    const c = colors[fileType] || "var(--color-text-muted)";
    return (
      <svg width="28" height="32" viewBox="0 0 28 32" fill="none" className="flex-shrink-0">
        {/* Page outline */}
        <path
          d="M2 3a2 2 0 012-2h13l9 9v19a2 2 0 01-2 2H4a2 2 0 01-2-2V3z"
          stroke={c}
          strokeWidth="1.5"
          fill="none"
        />
        {/* Fold */}
        <path
          d="M17 1v6a2 2 0 002 2h6"
          stroke={c}
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
        {/* Content lines */}
        <path
          d="M7 15h14M7 19h14M7 23h8"
          stroke={c}
          strokeWidth="1.3"
          strokeLinecap="round"
          opacity="0.5"
        />
      </svg>
    );
  };

  const mountedRef = useRef(false);

  // Plain function — recreated every render, always reads current state directly
  const buildFetchOptions = () => {
    const opts = {
      pageSize: PAGE_SIZE,
      sortBy,
      sortOrder,
    };
    // When any filter is active, search across ALL folders in the collection
    if (hasActiveFilter) {
      opts.folderId = "all";
    }
    if (nameFilter) opts.name = nameFilter;
    if (fileTypeFilter !== "all") opts.fileType = fileTypeFilter;
    if (createdByFilter) opts.createdBy = createdByFilter;
    if (lastUpdatedByFilter) opts.lastUpdatedBy = lastUpdatedByFilter;
    if (createdAtFilter.startDate) opts.createdAtStart = new Date(createdAtFilter.startDate).getTime() / 1000;
    if (createdAtFilter.endDate) opts.createdAtEnd = new Date(createdAtFilter.endDate + "T23:59:59").getTime() / 1000;
    if (lastUpdatedAtFilter.startDate) opts.updatedAtStart = new Date(lastUpdatedAtFilter.startDate).getTime() / 1000;
    if (lastUpdatedAtFilter.endDate) opts.updatedAtEnd = new Date(lastUpdatedAtFilter.endDate + "T23:59:59").getTime() / 1000;
    if (campaignStartDateFilter.startDate) opts.campaignStartFrom = new Date(campaignStartDateFilter.startDate).getTime() / 1000;
    if (campaignStartDateFilter.endDate) opts.campaignStartTo = new Date(campaignStartDateFilter.endDate + "T23:59:59").getTime() / 1000;
    if (campaignEndDateFilter.startDate) opts.campaignEndFrom = new Date(campaignEndDateFilter.startDate).getTime() / 1000;
    if (campaignEndDateFilter.endDate) opts.campaignEndTo = new Date(campaignEndDateFilter.endDate + "T23:59:59").getTime() / 1000;
    if (campaignStatusFilter !== "all") opts.campaignStatus = campaignStatusFilter;
    if (chatFilter !== "all") opts.chatId = chatFilter;
    return opts;
  };

  // Plain function — recreated every render with fresh buildFetchOptions
  const triggerFetch = (debounce = false) => {
    if (!onFetchFiles) return;
    const opts = buildFetchOptions();
    const doFetch = () => {
      onFetchFiles(opts);
    };
    if (debounce) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(doFetch, 300);
    } else {
      doFetch();
    }
  };

  // Trigger fetch when non-text filters or sort change (skip initial mount)
  useEffect(() => {
    if (!mountedRef.current) return;
    triggerFetch(false);
  }, [sortBy, sortOrder, fileTypeFilter, campaignStatusFilter, chatFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger fetch with debounce when text filters change (skip initial mount)
  useEffect(() => {
    if (!mountedRef.current) return;
    triggerFetch(true);
    return () => clearTimeout(debounceRef.current);
  }, [nameFilter, createdByFilter, lastUpdatedByFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger fetch when date filters change (skip initial mount)
  useEffect(() => {
    if (!mountedRef.current) return;
    triggerFetch(false);
  }, [createdAtFilter, lastUpdatedAtFilter, campaignStartDateFilter, campaignEndDateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial fetch on mount — MUST be declared LAST so change effects above skip (mountedRef is still false)
  useEffect(() => {
    if (onFetchFiles) {
      onFetchFiles(buildFetchOptions());
    }
    mountedRef.current = true;
    return () => { mountedRef.current = false; }; // reset on cleanup so Strict Mode re-mount also skips change effects
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll: IntersectionObserver on sentinel
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || !onLoadMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore]);

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new sort field with ascending order
      setSortBy(newSortBy);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    const isActive = sortBy === field;
    if (!isActive) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sortOrder === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const handleDeleteClick = (filePath) => {
    const file = files.find((f) => f.path === filePath);
    setFileToDelete(file);
    setShowSingleDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    setDeletingFile(fileToDelete.path);
    setShowSingleDeleteConfirm(false);
    setError(null);

    // Initialize delete queue for single file
    initDeleteQueue([fileToDelete.path], files);
    updateFileInDeleteQueue(0, { status: "deleting" });

    try {
      // Numa campanha da VTEX isto rejeita para a loja escolhida, e o backend recusa se
      // houver mais do que uma loja e nenhuma estiver escolhida.
      const result = await fileApi.deleteFile(
        fileToDelete.path,
        collectionName,
        chatFilter !== "all" ? chatFilter : null
      );

      if (result.success) {
        // Numa campanha da VTEX o gesto é uma rejeição PARA ESTA LOJA, e o ficheiro é
        // partilhado: só sai quando não sobra loja nenhuma a publicá-lo, e é o
        // `file_removed` que diz se saiu. Com ele a falso a linha TEM de ficar, senão
        // anunciava-se uma remoção que não houve e a releitura trazia-a de volta.
        const rejeitada = result.action === "rejected";
        const ficheiroSaiu = !rejeitada || result.file_removed;
        updateFileInDeleteQueue(0, { status: ficheiroSaiu ? "success" : "rejected" });
        if (ficheiroSaiu && onOptimisticRemoveFiles) onOptimisticRemoveFiles([fileToDelete.path]);
        // A rejeição diz-se sempre, mesmo com o ficheiro fora: é ela que explica
        // porque é que a campanha não volta na sincronização seguinte.
        toast.success(
          translate(
            rejeitada
              ? ficheiroSaiu
                ? "campaignRejectedAndRemoved"
                : "campaignRejectedForStore"
              : "deletedFileSuccessfully"
          )
        );
      } else {
        const errorMsg = result.message || "Unknown error";
        updateFileInDeleteQueue(0, { status: "error", error: errorMsg });
        toast.error(translate("failedToDeleteFile", { error: errorMsg }));
      }
    } catch (error) {
      // O detalhe do servidor já vinha para a fila; passa também para o toast, porque
      // é lá que se lê. Uma campanha da VTEX partilhada por duas lojas é recusada com
      // 409 e a frase diz para escolher a loja no filtro.
      const errorMsg = error.response?.data?.detail || error.message;
      updateFileInDeleteQueue(0, { status: "error", error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setDeletingFile(null);
      setFileToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    handleCloseSingleDeleteConfirm();
  };

  const handleViewFile = (file) => {
    setViewingFile(file);
    setShowFileViewer(true);
    setStartInEditMode(false);
  };

  const handleCloseFileViewer = () => {
    setShowFileViewer(false);
    setViewingFile(null);
    setStartInEditMode(false);
  };

  const handleEditFile = (file) => {
    // Only allow editing for text and markdown files
    if (file.file_type === "text" || file.file_type === "markdown") {
      setViewingFile(file);
      setShowFileViewer(true);
      // We'll use a flag to indicate this should start in edit mode
      setStartInEditMode(true);
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      if (file.file_type === "pdf") {
        // For PDFs, get the blob and download
        const blob = await fileApi.getFileContentAsBlob(
          file.path,
          collectionName
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the URL after a short delay to ensure download starts
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        // For text files, get the content and create a blob
        const response = await fileApi.getFileContent(
          file.path,
          collectionName
        );
        const blob = new Blob([response.content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the URL after a short delay to ensure download starts
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(
        "Failed to download file: " +
          (error.response?.data?.detail || error.message)
      );
    }
  };

  // Multiple file selection handlers
  const handleSelectFile = (filePath) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(filePath)) {
      newSelected.delete(filePath);
    } else {
      newSelected.add(filePath);
    }
    setSelectedFiles(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === files.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(files.map((f) => f.path)));
    }
  };

  // Folder selection handlers
  const handleSelectFolder = (folderId) => {
    const newSelected = new Set(selectedFolders);
    if (newSelected.has(folderId)) {
      newSelected.delete(folderId);
    } else {
      newSelected.add(folderId);
    }
    setSelectedFolders(newSelected);
  };

  const handleSelectAllFolders = () => {
    if (selectedFolders.size === folders.length) {
      setSelectedFolders(new Set());
    } else {
      setSelectedFolders(new Set(folders.map((f) => f.id)));
    }
  };

  const handleBulkFolderDeleteClick = async () => {
    const folderIds = Array.from(selectedFolders);
    // Show loading state on selected folders while fetching counts
    setSavingFolderIds((prev) => new Set([...prev, ...folderIds]));
    let totalSubFolders = 0;
    let totalSubFiles = 0;
    try {
      const results = await Promise.all(folderIds.map((id) => folderApi.getFolderContentsCount(id).catch(() => ({ folders_count: 0, files_count: 0 }))));
      for (const r of results) {
        totalSubFolders += r.folders_count || 0;
        totalSubFiles += r.files_count || 0;
      }
    } catch { /* ignore */ }
    setSavingFolderIds((prev) => { const next = new Set(prev); folderIds.forEach((id) => next.delete(id)); return next; });
    setBulkFolderDeleteCounts({ totalFolders: totalSubFolders, totalFiles: totalSubFiles });
    setShowBulkFolderDeleteConfirm(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleBulkFolderDeleteConfirm = async () => {
    setBulkFolderDeleting(true);
    setShowBulkFolderDeleteConfirm(false);
    const folderIds = Array.from(selectedFolders);
    for (const id of folderIds) {
      setSavingFolderIds((prev) => new Set([...prev, id]));
      try {
        await folderApi.deleteFolder(id);
      } catch (err) {
        toast.error(err.response?.data?.detail || translate("failedToDeleteFolder"));
      } finally {
        setSavingFolderIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      }
    }
    setSelectedFolders(new Set());
    setBulkFolderDeleting(false);
    setBulkFolderDeleteCounts(null);
    setFolders((prev) => prev.filter((f) => !folderIds.includes(f.id)));
    setTotalFolders((prev) => Math.max(0, prev - folderIds.length));
    if (onOptimisticRemoveFolders) onOptimisticRemoveFolders(folderIds);
  };

  const handleBulkFolderDownload = async () => {
    const folderIds = Array.from(selectedFolders);
    const folderObjects = folders.filter((f) => folderIds.includes(f.id));

    // Single folder — reuse existing handler
    if (folderObjects.length === 1) {
      handleDownloadFolder(folderObjects[0]);
      return;
    }

    // Multiple folders — one ZIP with all folders
    let downloaded = 0;
    const progress = createProgressToast(translate("preparingDownload"));

    try {
      const zip = new JSZip();

      // Phase 1: Collect all files with their zip paths
      const allFiles = [];
      const collectFiles = async (parentId, pathPrefix) => {
        let cursor = null;
        do {
          const opts = { folderId: parentId, cursor };
          const result = await fileApi.listFiles(collectionName, null, opts);
          for (const file of (result.files || [])) {
            allFiles.push({ file, zipPath: `${pathPrefix}/${file.name}` });
          }
          progress.update(`${translate("preparingDownload")} (${allFiles.length})`);
          cursor = result.next_cursor;
        } while (cursor);

        let folderCursor = null;
        do {
          const opts = { limit: 50 };
          if (folderCursor) opts.cursor = folderCursor;
          const result = await folderApi.listFolders(collectionName, parentId, opts);
          for (const sub of (result.folders || [])) {
            await collectFiles(sub.id, `${pathPrefix}/${sub.name}`);
          }
          folderCursor = result.next_cursor;
        } while (folderCursor);
      };

      for (const f of folderObjects) {
        await collectFiles(f.id, f.name);
      }

      // A pasta está vazia se NADA foi recolhido, e não se o contador da pasta diz
      // zero. Esse contador conta o que existe no disco, incluindo as campanhas da
      // VTEX por aprovar, que o backend deixou de listar (RPAA-229): numa pasta com 44
      // em fila e nada aprovado dizia 44, esta guarda não disparava, e saía um ZIP
      // vazio com um toast de sucesso. O que se pode descarregar é o que se consegue
      // listar, portanto é a recolha que decide.
      if (allFiles.length === 0) {
        progress.dismiss();
        toast.error(translate("emptyFolder"));
        return;
      }

      // Phase 2: Download all files in parallel
      const tasks = allFiles.map(({ file, zipPath }) => () => downloadFileToZip(zip, file, zipPath));
      await runWithConcurrency(tasks, DOWNLOAD_CONCURRENCY, (done) => {
        downloaded = done;
        progress.update(`${translate("downloadingFiles")} (${done}/${allFiles.length})`);
      });

      progress.update(translate("creatingZip"));
      await new Promise((r) => setTimeout(r, 50));
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${collectionName}_folders_${new Date().toISOString().split("T")[0]}.zip`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);

      progress.dismiss();
      toast.success(translate("downloadZipSuccess", { count: downloaded, plural: downloaded !== 1 ? "s" : "" }));
    } catch (err) {
      progress.dismiss();
      toast.error(translate("downloadZipError"));
      console.error("Error downloading folders:", err);
    }
  };

  const handleBulkFolderMove = () => {
    setShowMoveModal({ type: "folders", ids: Array.from(selectedFolders), name: `${selectedFolders.size} ${selectedFolders.size === 1 ? translate("folderSingular") : translate("folderPlural")}` });
  };

  const handleBulkFileMove = () => {
    setShowMoveModal({
      type: "files",
      ids: Array.from(selectedFiles),
      name: `${selectedFiles.size} ${selectedFiles.size === 1 ? translate("fileSingular") : translate("filePlural")}`,
    });
  };

  const handleBulkDeleteClick = () => {
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkDeleteConfirm = async () => {
    setBulkDeleting(true);
    setShowBulkDeleteConfirm(false);
    setError(null);

    const filesToDelete = Array.from(selectedFiles);

    // Initialize delete queue
    initDeleteQueue(filesToDelete, files);

    const results = [];
    const errors = [];
    // Campanhas da VTEX: quantas foram rejeitadas, e quantas dessas ficaram na lista
    // por outra loja continuar a publicá-las.
    let rejectedCount = 0;
    let keptCount = 0;

    for (let i = 0; i < filesToDelete.length; i++) {
      const filePath = filesToDelete[i];
      updateFileInDeleteQueue(i, { status: "deleting" });

      try {
        // Numa campanha da VTEX isto rejeita para a loja escolhida, e o backend recusa se
        // houver mais do que uma loja e nenhuma estiver escolhida.
        const result = await fileApi.deleteFile(
          filePath,
          collectionName,
          chatFilter !== "all" ? chatFilter : null
        );
        if (result.success) {
          // `results` é o que alimenta a remoção optimista das linhas, portanto leva
          // tudo o que saiu mesmo: os ficheiros normais, e as campanhas que a
          // rejeição deixou sem loja nenhuma. Uma campanha que outra loja continua a
          // publicar fica de fora, que a linha dela permanece.
          const rejeitada = result.action === "rejected";
          const ficheiroSaiu = !rejeitada || result.file_removed;
          if (rejeitada) rejectedCount += 1;
          if (ficheiroSaiu) results.push(filePath);
          else keptCount += 1;
          updateFileInDeleteQueue(i, { status: ficheiroSaiu ? "success" : "rejected" });
        } else {
          errors.push({
            path: filePath,
            error: result.message || "Unknown error",
          });
          updateFileInDeleteQueue(i, {
            status: "error",
            error: result.message || "Unknown error",
          });
        }
      } catch (error) {
        const errMsg = error.response?.data?.detail || error.message;
        errors.push({ path: filePath, error: errMsg });
        updateFileInDeleteQueue(i, { status: "error", error: errMsg });
      }
    }

    setSelectedFiles(new Set());
    setBulkDeleting(false);

    // Optimistically remove successfully deleted files
    if (results.length > 0 && onOptimisticRemoveFiles) {
      onOptimisticRemoveFiles(results);
    }

    if (errors.length === 0) {
      if (rejectedCount > 0 && rejectedCount === filesToDelete.length) {
        // Só campanhas: o que interessa dizer é que ficaram rejeitadas, e não quantos
        // ficheiros saíram — é a rejeição que as mantém fora da sincronização
        // seguinte. Com uma delas a ficar, a frase tem de ser a outra.
        const saiu = keptCount === 0;
        toast.success(
          rejectedCount === 1
            ? translate(saiu ? "campaignRejectedAndRemoved" : "campaignRejectedForStore")
            : translate(saiu ? "campaignsRejectedAndRemoved" : "campaignsRejectedForStore", {
                count: rejectedCount,
              })
        );
      } else {
        toast.success(results.length === 1
          ? translate("deletedFileSuccessfully")
          : translate("deletedFilesSuccessfully", { count: results.length }));
      }
    } else if (results.length > 0) {
      toast.error(
        translate("deletedFilesPartial", {
          successful: results.length,
          total: filesToDelete.length,
          failed: errors.length,
        }),
        { duration: 6000 }
      );
    } else {
      toast.error(
        () => (
          <div style={{ fontSize: "13px" }}>
            <div style={{ fontWeight: 600, marginBottom: "6px" }}>
              {translate("failedToDeleteFile", { error: "" }).replace(/:\s*$/, "")}
            </div>
            <ul style={{ margin: 0, paddingLeft: "16px", listStyle: "disc" }}>
              {errors.map((e, i) => (
                <li key={i} style={{ marginBottom: "2px" }}>
                  <span style={{ fontWeight: 500 }}>{e.path.split("/").pop()}</span>{" "}
                  <span style={{ opacity: 0.7 }}>— {e.error}</span>
                </li>
              ))}
            </ul>
          </div>
        ),
        { duration: 8000 }
      );
    }
  };

  const handleBulkDeleteCancel = () => {
    handleCloseBulkDeleteConfirm();
  };

  // Persistent loading toast — updates text without exit/enter animation
  const createProgressToast = (initialMessage) => {
    const id = toast.loading(initialMessage);
    return {
      id,
      update: (msg) => { toast.loading(msg, { id }); },
      dismiss: () => { toast.dismiss(id); },
    };
  };

  // Download a single file and add to ZIP, returns { success, path, error? }
  const downloadFileToZip = async (zip, file, zipPath) => {
    try {
      if (file.file_type === "pdf") {
        const blob = await fileApi.getFileContentAsBlob(file.path, collectionName);
        zip.file(zipPath, blob);
      } else {
        const response = await fileApi.getFileContent(file.path, collectionName);
        zip.file(zipPath, response.content);
      }
      return { success: true, path: file.path };
    } catch (error) {
      console.error(`Error adding ${file.name} to ZIP:`, error);
      return { success: false, path: file.path, error: error.response?.data?.detail || error.message };
    }
  };

  // Run async tasks with a concurrency limit
  const runWithConcurrency = async (tasks, limit, onProgress) => {
    const results = [];
    let completed = 0;
    let index = 0;

    const runNext = async () => {
      while (index < tasks.length) {
        const i = index++;
        const result = await tasks[i]();
        results[i] = result;
        completed++;
        if (onProgress) onProgress(completed);
      }
    };

    const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => runNext());
    await Promise.all(workers);
    return results;
  };

  const DOWNLOAD_CONCURRENCY = 5;

  const handleBulkDownload = async () => {
    const filesToDownload = Array.from(selectedFiles);

    // If only one file is selected, download it directly
    if (filesToDownload.length === 1) {
      const file = files.find((f) => f.path === filesToDownload[0]);
      if (file) {
        await handleDownloadFile(file);
      }
      return;
    }

    // For multiple files, create a ZIP
    const total = filesToDownload.length;
    const progress = createProgressToast(`${translate("downloadingFiles")} (0/${total})`);

    try {
      const zip = new JSZip();

      const tasks = filesToDownload.map((filePath) => () => {
        const file = files.find((f) => f.path === filePath);
        if (!file) return Promise.resolve({ success: false, path: filePath, error: "File not found" });
        return downloadFileToZip(zip, file, file.name);
      });

      const results = await runWithConcurrency(tasks, DOWNLOAD_CONCURRENCY, (done) => {
        progress.update(`${translate("downloadingFiles")} (${done}/${total})`);
      });

      const successes = results.filter((r) => r.success);
      const errors = results.filter((r) => !r.success);

      // Generate ZIP and download
      if (successes.length > 0) {
        progress.update(translate("creatingZip"));
        await new Promise((r) => setTimeout(r, 50));
        const zipBlob = await zip.generateAsync({ type: "blob" });
        progress.dismiss();
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${collectionName}_files_${
          new Date().toISOString().split("T")[0]
        }.zip`;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);

        if (errors.length === 0) {
          toast.success(translate("downloadZipSuccess", { count: successes.length }));
        } else {
          toast.error(translate("downloadZipPartial", { successful: successes.length, failed: errors.length }));
        }
      } else {
        progress.dismiss();
        toast.error(translate("downloadZipError"));
      }
    } catch (error) {
      console.error("Error creating ZIP:", error);
      progress.dismiss();
      toast.error(translate("downloadZipError"));
    }
  };

  // Download folder as ZIP with folder structure preserved
  const handleDownloadFolder = async (folder) => {
    setFolderMenuOpen(null);
    let downloaded = 0;
    const progress = createProgressToast(translate("preparingDownload"));

    try {
      const zip = new JSZip();

      // Phase 1: Collect all files with their zip paths
      const allFiles = [];
      const collectFiles = async (parentId, pathPrefix) => {
        let cursor = null;
        do {
          const opts = { folderId: parentId, cursor };
          const result = await fileApi.listFiles(collectionName, null, opts);
          for (const file of (result.files || [])) {
            allFiles.push({ file, zipPath: `${pathPrefix}/${file.name}` });
          }
          progress.update(`${translate("preparingDownload")} (${allFiles.length})`);
          cursor = result.next_cursor;
        } while (cursor);

        let folderCursor = null;
        do {
          const opts = { limit: 50 };
          if (folderCursor) opts.cursor = folderCursor;
          const result = await folderApi.listFolders(collectionName, parentId, opts);
          for (const sub of (result.folders || [])) {
            await collectFiles(sub.id, `${pathPrefix}/${sub.name}`);
          }
          folderCursor = result.next_cursor;
        } while (folderCursor);
      };

      await collectFiles(folder.id, folder.name);

      // A pasta está vazia se NADA foi recolhido, e não se o contador da pasta diz
      // zero. Esse contador conta o que existe no disco, incluindo as campanhas da
      // VTEX por aprovar, que o backend deixou de listar (RPAA-229): numa pasta com 44
      // em fila e nada aprovado dizia 44, esta guarda não disparava, e saía um ZIP
      // vazio com um toast de sucesso. O que se pode descarregar é o que se consegue
      // listar, portanto é a recolha que decide.
      if (allFiles.length === 0) {
        progress.dismiss();
        toast.error(translate("emptyFolder"));
        return;
      }

      // Phase 2: Download all files in parallel
      const tasks = allFiles.map(({ file, zipPath }) => () => downloadFileToZip(zip, file, zipPath));
      await runWithConcurrency(tasks, DOWNLOAD_CONCURRENCY, (done) => {
        downloaded = done;
        progress.update(`${translate("downloadingFiles")} (${done}/${allFiles.length})`);
      });

      progress.update(translate("creatingZip"));
      await new Promise((r) => setTimeout(r, 50));
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${folder.name}.zip`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);

      progress.dismiss();
      toast.success(translate("downloadZipSuccess", { count: downloaded, plural: downloaded !== 1 ? "s" : "" }));
    } catch (err) {
      progress.dismiss();
      toast.error(translate("downloadZipError"));
      console.error("Error downloading folder:", err);
    }
  };

  // Campaign date management functions

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <span className="badge-active px-2 py-0.5 text-xs rounded-full">
            {translate("campaignActive")}
          </span>
        );
      case "pending":
        return (
          <span className="badge-inactive px-2 py-0.5 text-xs rounded-full">
            {translate("campaignPending")}
          </span>
        );
      case "expired":
        return (
          <span className="px-2 py-0.5 text-xs rounded-full bg-[rgba(239,68,68,0.1)] text-[var(--color-error)]">
            {translate("campaignExpired")}
          </span>
        );
      default:
        return null;
    }
  };

  const handleOpenDateModal = (file) => {
    setEditingFile(file);
    // Legacy rows hold bare strings; toEntries makes them editable without a migration.
    setModalSourceUrls(toEntries(file.metadata?.source_urls ?? []));
    const startDate = timestampToDate(
      file.metadata?.ai_agent_knowledge_start_date
    );
    const endDate = timestampToDate(file.metadata?.ai_agent_knowledge_end_date);
    setModalStartDate(startDate);
    setModalEndDate(endDate);
    modalEndDateRef.current = endDate; // Update ref immediately
    // Initialize hasCustomTimeRef based on existing endDate
    if (endDate) {
      const isDefaultTime =
        endDate.getHours() === 23 && endDate.getMinutes() === 59;
      hasCustomTimeRef.current = !isDefaultTime;
    } else {
      hasCustomTimeRef.current = false;
    }
    setShowDateModal(true);
  };

  const handleSaveCampaignDates = async () => {
    if (!editingFile) return;

    // Validation
    if (modalStartDate && modalEndDate && modalStartDate >= modalEndDate) {
      toast.error(translate("startDateMustBeBeforeEndDate"));
      return;
    }

    const errorKey = validateLinks(modalSourceUrls);
    if (errorKey) {
      toast.error(translate(errorKey, { max: MAX_SOURCE_LINKS }));
      return;
    }
    const cleanedLinks = normalizeLinks(modalSourceUrls);

    setSavingDates(true);

    try {
      const startDateISO = modalStartDate ? modalStartDate.toISOString() : null;
      const endDateISO = modalEndDate ? modalEndDate.toISOString() : null;

      // Three-state argument for source_urls:
      //   undefined: unchanged → don't send the field
      //   []:        clear → send an empty array (server clears column + payload)
      //   [...]:     replace
      const original = editingFile.metadata?.source_urls ?? [];
      // Deliberately compares the RAW stored list against the normalized edited one, so a
      // stored list that was never normalized counts as changed and saving self-heals it.
      // A legacy bare string therefore never equals {url}, which is exactly how the 138
      // production rows convert to the new shape: on their next save.
      // JSON.stringify rather than concatenating the two fields: any separator (or none)
      // lets a url and a label swap content and still produce the same key, so a real edit
      // would be silently discarded as "unchanged".
      // The str:/obj: discriminator keeps the two branches disjoint, so the claim above holds
      // for any stored string, not merely for ones that happen to look like URLs.
      const asKey = (entry) =>
        typeof entry === "string"
          ? `str:${entry}`
          : `obj:${JSON.stringify([entry?.url || "", entry?.label || ""])}`;
      const unchanged =
        original.length === cleanedLinks.length &&
        original.every((entry, i) => asKey(entry) === asKey(cleanedLinks[i]));
      const sourceUrlsArg = unchanged ? undefined : cleanedLinks;

      await fileApi.updateFileMetadata(
        editingFile.path,
        startDateISO,
        endDateISO,
        collectionName,
        sourceUrlsArg
      );

      toast.success(translate("datesUpdatedSuccessfully"));

      // Close modal and clear state
      setShowDateModal(false);
      setEditingFile(null);
      setModalStartDate(null);
      setModalEndDate(null);
      setModalSourceUrls([]);

      // Refresh file list to get updated metadata
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error updating file dates:", error);
      toast.error(
        translate("failedToUpdateDates") +
          ": " +
          (error.response?.data?.detail || error.message)
      );
    } finally {
      setSavingDates(false);
    }
  };

  const handleCloseDateModal = () => {
    setShowDateModal(false);
    setEditingFile(null);
    setModalStartDate(null);
    setModalEndDate(null);
    setModalSourceUrls([]);
  };

  const dateModalRef = useModal(showDateModal, handleCloseDateModal);

  // Chat management functions
  const handleOpenChatModal = (file) => {
    setEditingFileChats(file);
    setSelectedFileChats(file.chat_ids || []);
    setShowChatModal(true);

    // Chats are already loaded by FileManagement component
    if (availableChats.length === 0) {
      toast.error(translate("noChatsAvailableMessage"));
    }
  };

  const handleCloseChatModal = () => {
    setShowChatModal(false);
    setEditingFileChats(null);
    setSelectedFileChats([]);
  };

  const chatModalRef = useModal(showChatModal, handleCloseChatModal);

  const toggleChatSelection = (chatId) => {
    if (selectedFileChats.includes(chatId)) {
      setSelectedFileChats(selectedFileChats.filter((id) => id !== chatId));
    } else {
      setSelectedFileChats([...selectedFileChats, chatId]);
    }
  };

  // Mostra-se com mais do que um chat activo, e SEMPRE quando o ficheiro não tem
  // chat nenhum: um ficheiro sem chat é invisível ao agente, e com uma só loja o
  // aviso ficava escondido exactamente no caso em que mais importa.
  //
  // Já NÃO é por causa das promoções da VTEX por aprovar. Essas deixaram de chegar a
  // esta lista (RPAA-229: o `hide_unassigned_created_by` que o `list-files` passa ao
  // `list_files_paginated`), e ainda bem, porque este mesmo menu oferece "Gerir
  // Chats", que chama o `addChatToFile` — o MESMO pedido que o botão Aprovar do
  // painel. A regra fica pelo caso que sobra: um ficheiro que alguém carregou e ficou
  // sem chat, que é uma avaria e tem de poder reparar-se daqui.
  const canManageChats = (file) =>
    availableChats.filter((chat) => chat.is_active).length > 1 || !(file.chat_ids?.length > 0);

  const getChatName = (chatId) => {
    const chat = availableChats.find((c) => c.id === chatId);
    return chat ? chat.name : chatId;
  };

  const handleSaveFileChats = async () => {
    if (!editingFileChats) return;

    // Validate that at least one chat is selected
    if (selectedFileChats.length === 0) {
      toast.error(translate("selectAtLeastOneChat"));
      return;
    }

    try {
      setSavingChats(true);

      const currentChatIds = editingFileChats.chat_ids || [];
      const chatsToAdd = selectedFileChats.filter(
        (chatId) => !currentChatIds.includes(chatId)
      );
      const chatsToRemove = currentChatIds.filter(
        (chatId) => !selectedFileChats.includes(chatId)
      );

      // Add new chats
      for (const chatId of chatsToAdd) {
        await fileApi.addChatToFile(
          editingFileChats.path,
          chatId,
          collectionName
        );
      }

      // Remove chats
      for (const chatId of chatsToRemove) {
        await fileApi.removeChatFromFile(
          editingFileChats.path,
          chatId,
          collectionName
        );
      }

      toast.success(translate("chatsUpdatedSuccessfully"));
      handleCloseChatModal();

      // Refresh file list
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error updating file chats:", error);
      toast.error(
        error.response?.data?.detail || translate("failedToUpdateChats")
      );
    } finally {
      setSavingChats(false);
    }
  };

  const handleCloseBulkDeleteConfirm = () => {
    setShowBulkDeleteConfirm(false);
  };

  const handleCloseSingleDeleteConfirm = () => {
    setShowSingleDeleteConfirm(false);
    setFileToDelete(null);
  };

  const bulkDeleteModalRef = useModal(
    showBulkDeleteConfirm,
    handleCloseBulkDeleteConfirm
  );
  const singleDeleteModalRef = useModal(
    showSingleDeleteConfirm,
    handleCloseSingleDeleteConfirm
  );
  // eslint-disable-next-line no-unused-vars
  const bulkFolderDeleteModalRef = useModal(
    showBulkFolderDeleteConfirm,
    () => { setShowBulkFolderDeleteConfirm(false); setBulkFolderDeleteCounts(null); }
  );


  return (
    <div>
      {/* Error Display */}
      {error && (
        <div className="px-6 py-4 border-b border-[var(--color-border)]" style={{ backgroundColor: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.15)' }}>
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--color-error)]">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ─── Folders section (above toolbar, no filters apply) ─── */}
      {!hasActiveFilter && (folders.length > 0 || creatingFolder || foldersLoading) && (
        <div className="divide-y divide-[var(--color-border)] border-b border-[var(--color-border)]">
          {/* Folder toolbar: counter + bulk actions */}
          <div className="px-6 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <p className="text-xs text-[var(--color-text-muted)]">
                {totalFolders > 0 ? `${totalFolders} ${totalFolders === 1 ? translate("folderSingular") : translate("folderPlural")}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selectedFolders.size > 0 && (
                <>
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {translate("selected", { count: selectedFolders.size, plural: selectedFolders.size !== 1 ? "s" : "" })}
                  </span>
                  <button onClick={handleBulkFolderMove} className="btn-secondary flex items-center space-x-1 px-3 py-1 text-xs">
                    <FolderInput size={14} />
                    <span>{translate("moveTo")}</span>
                  </button>
                  <button onClick={handleBulkFolderDownload} className="btn-primary flex items-center space-x-1 px-3 py-1 text-sm">
                    <Download size={14} />
                    <span>{translate("downloadAll")}</span>
                  </button>
                  <button onClick={handleBulkFolderDeleteClick} disabled={bulkFolderDeleting} className="btn-destructive flex items-center space-x-1 px-3 py-1 text-sm disabled:opacity-50">
                    <Trash size={14} />
                    <span>{translate("deleteSelected")}</span>
                  </button>
                </>
              )}
            </div>
          </div>
          {/* Select All folders */}
          {folders.length > 0 && (
            <div className="px-6 py-2">
              <label className="flex items-center space-x-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedFolders.size === folders.length && folders.length > 0}
                  onChange={handleSelectAllFolders}
                  className="rounded border-[var(--color-border)]"
                />
                <span>
                  {selectedFolders.size === folders.length && folders.length > 0
                    ? translate("deselectAll")
                    : translate("selectAll")}
                </span>
              </label>
            </div>
          )}
          {/* Inline folder creation row */}
          {creatingFolder && (
            <div
              className={`px-6 py-5 flex items-center gap-4${savingFolder ? " animate-pulse pointer-events-none" : ""}`}
              style={{ opacity: savingFolder ? 0.4 : 1 }}
            >
              <Folder size={28} style={{ color: "var(--color-mint)" }} />
              {savingFolder ? (
                <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                  {newFolderName}
                </span>
              ) : (
                <>
                  <input
                    ref={newFolderInputRef}
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); handleCreateFolder(); }
                      if (e.key === "Escape") { setCreatingFolder(false); setNewFolderName(""); }
                    }}
                    placeholder={translate("folderName")}
                    className="field-input text-sm flex-1"
                    autoFocus
                  />
                  <button onClick={handleCreateFolder} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-raised)]">
                    <Check size={16} style={{ color: "var(--color-mint)" }} />
                  </button>
                  <button onClick={() => { setCreatingFolder(false); setNewFolderName(""); }} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-raised)]">
                    <X size={16} style={{ color: "var(--color-text-muted)" }} />
                  </button>
                </>
              )}
            </div>
          )}
          {/* Folder rows */}
          {folders.map((folder) => {
            const isFolderBusy = movingFolderIds.has(folder.id) || savingFolderIds.has(folder.id) || loadingDeleteFolderId === folder.id;
            return (
              <div
                key={folder.id}
                className={`px-6 py-4 flex items-center gap-4 cursor-pointer transition-all ${isFolderBusy ? "animate-pulse pointer-events-none" : ""}`}
                style={{
                  opacity: isFolderBusy ? 0.4 : 1,
                  backgroundColor: dragOverFolderId === folder.id ? "rgba(62, 207, 142, 0.10)" : undefined,
                  boxShadow: dragOverFolderId === folder.id ? "inset 0 0 0 2px var(--color-mint)" : undefined,
                  borderRadius: dragOverFolderId === folder.id ? "8px" : undefined,
                }}
                onClick={() => !isFolderBusy && onNavigateToFolder && onNavigateToFolder(folder.id)}
                draggable={!isFolderBusy}
                onDragStart={(e) => {
                  if (isFolderBusy) return;
                  handleDragStart(e, "folder", folder.id);
                }}
                onDragEnd={() => { if (onDraggingChange) onDraggingChange(false); }}
                onDragOver={(e) => { e.preventDefault(); if (!draggingFolderIds.has(folder.id)) setDragOverFolderId(folder.id); }}
                onDragLeave={() => setDragOverFolderId(null)}
                onDrop={(e) => handleFolderDrop(e, folder.id)}
                onContextMenu={(e) => {
                  // Links and text fields keep their native menu (open-in-new-tab, paste); checkboxes don't.
                  if (e.target.closest?.('a[href], textarea, input:not([type="checkbox"]):not([type="radio"])')) return;
                  // Right-click inside our own open menu: keep it put, block the native one.
                  if (menuRef.current && menuRef.current.contains(e.target)) {
                    e.preventDefault();
                    return;
                  }
                  // Match when the three-dot button is unavailable; the busy check is belt-and-braces
                  // (the row is already pointer-events-none while busy).
                  if (isFolderBusy || isDraggingItem || renamingFolderId === folder.id) return;
                  e.preventDefault();
                  triggerRef.current = cursorAnchor(e.clientX, e.clientY);
                  setMenuPosition(computeMenuPlaceholder(triggerRef.current.getBoundingClientRect()));
                  setMenuAnchorNonce((n) => n + 1);
                  setFolderMenuOpen(folder.id);
                  setFileMenuOpen(null);
                }}
                onMouseEnter={(e) => { if (!isFolderBusy && dragOverFolderId !== folder.id) e.currentTarget.style.backgroundColor = "var(--color-mint-muted)"; }}
                onMouseLeave={(e) => { if (dragOverFolderId !== folder.id) e.currentTarget.style.backgroundColor = ""; }}
              >
                <input
                  type="checkbox"
                  checked={selectedFolders.has(folder.id)}
                  onChange={() => handleSelectFolder(folder.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-[var(--color-border)] flex-shrink-0"
                  disabled={isFolderBusy}
                />
                <Folder
                  size={28}
                  style={{
                    color: dragOverFolderId === folder.id ? "var(--color-mint)" : "var(--color-mint)",
                    opacity: dragOverFolderId === folder.id ? 1 : 0.7,
                  }}
                />
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  {renamingFolderId === folder.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        ref={renameFolderInputRef}
                        type="text"
                        value={renameFolderName}
                        onChange={(e) => setRenameFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); handleRenameFolder(folder.id); }
                          if (e.key === "Escape") { setRenamingFolderId(null); setRenameFolderName(""); }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="field-input text-sm flex-1"
                        autoFocus
                      />
                      <button onClick={(e) => { e.stopPropagation(); handleRenameFolder(folder.id); }} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-raised)]">
                        <Check size={16} style={{ color: "var(--color-mint)" }} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setRenamingFolderId(null); setRenameFolderName(""); }} className="p-1.5 rounded-lg hover:bg-[var(--color-surface-raised)]">
                        <X size={16} style={{ color: "var(--color-text-muted)" }} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-[var(--color-text-primary)] truncate" title={folder.name}>
                        {folder.name}
                      </p>
                      {folder.created_by && (
                        <div className="relative">
                          <button
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              const rect = e.target.getBoundingClientRect();
                              setTooltipPosition({ top: rect.top - 10, left: rect.left });
                              setShowTooltip(`folder::${folder.id}`);
                            }}
                            onMouseLeave={() => setShowTooltip(null)}
                            className="p-0.5 rounded hover:bg-[var(--color-surface-raised)]"
                          >
                            <Info size={14} style={{ color: "var(--color-text-muted)" }} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Drop target label */}
                {(isDraggingItem || dragOverFolderId) && !isFolderBusy && renamingFolderId !== folder.id && !draggingFolderIds.has(folder.id) && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 transition-colors"
                    style={{
                      backgroundColor: dragOverFolderId === folder.id ? "var(--color-mint-muted)" : "var(--color-surface-raised)",
                      color: dragOverFolderId === folder.id ? "var(--color-mint-dark)" : "var(--color-text-muted)",
                      border: `1px solid ${dragOverFolderId === folder.id ? "var(--color-mint-border)" : "var(--color-border)"}`,
                    }}
                  >
                    {translate("dropHereToMove")}
                  </span>
                )}
                {/* Three-dot menu */}
                {!isDraggingItem && renamingFolderId !== folder.id && (
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (folderMenuOpen === folder.id) {
                          setFolderMenuOpen(null);
                        } else {
                          triggerRef.current = e.currentTarget;
                          setMenuPosition(computeMenuPlaceholder(e.currentTarget.getBoundingClientRect()));
                          setFolderMenuOpen(folder.id);
                          setFileMenuOpen(null);
                        }
                      }}
                      className="p-1.5 rounded-lg hover:bg-[var(--color-surface-raised)] transition-colors"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      <MoreVertical size={16} />
                    </button>
                    {folderMenuOpen === folder.id && (
                      <div
                        ref={menuRef}
                        className="fixed w-40 rounded-xl shadow-lg z-[9999] py-1"
                        style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", ...menuPosition }}
                      >
                        {locationWritable && (
                          <>
                            <button
                              className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-surface-raised)] flex items-center gap-2 transition-colors"
                              style={{ color: "var(--color-text-secondary)" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenamingFolderId(folder.id);
                                setRenameFolderName(folder.name);
                                setFolderMenuOpen(null);
                              }}
                            >
                              <Pencil size={14} />
                              {translate("rename")}
                            </button>
                            <button
                              className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-surface-raised)] flex items-center gap-2 transition-colors"
                              style={{ color: "var(--color-text-secondary)" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowMoveModal({ type: "folder", id: folder.id, name: folder.name });
                                setFolderMenuOpen(null);
                              }}
                            >
                              <FolderInput size={14} />
                              {translate("moveTo")}
                            </button>
                          </>
                        )}
                        <button
                          className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-surface-raised)] flex items-center gap-2 transition-colors"
                          style={{ color: "var(--color-text-secondary)" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFolder(folder);
                          }}
                        >
                          <Download size={14} />
                          {translate("download")}
                        </button>
                        {collectionName === "internal_procedures" && isInternalProceduresManager() && !folder.parent_folder_id && (
                          <button
                            className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-surface-raised)] flex items-center gap-2 transition-colors"
                            style={{ color: "var(--color-text-secondary)" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignRoleValue(folder.required_role || "");
                              setShowAssignRoleModal(folder);
                              setFolderMenuOpen(null);
                            }}
                          >
                            <Shield size={14} />
                            {translate("assignRole")}
                          </button>
                        )}
                        {locationWritable && (
                          <>
                            <div style={{ borderTop: "1px solid var(--color-border)", margin: "4px 0" }} />
                            <button
                              className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-surface-raised)] flex items-center gap-2 transition-colors"
                              style={{ color: "var(--color-error)" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFolderDeleteClick(folder);
                              }}
                            >
                              <Trash2 size={14} />
                              {translate("delete")}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {/* Folder infinite scroll sentinel */}
          {hasMoreFolders && (
            <div ref={folderSentinelRef} className="px-6 py-3">
              <div className="animate-pulse flex items-center gap-4">
                <div className="w-7 h-7 rounded bg-[var(--color-skeleton-light)]" />
                <div className="h-3 w-32 rounded bg-[var(--color-skeleton-light)]" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Queue panels (upload/delete progress) ─── */}
      {queuePanels}

      {/* ─── Files toolbar ─── */}
      {(totalFiles > 0 || files.length > 0 || hasActiveFilter) && (
      <div className="px-6 py-3 border-b border-[var(--color-border)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {(!isLoading || files.length > 0) && (totalFiles > 0 || hasActiveFilter) && (
              <p className="text-xs text-[var(--color-text-muted)]">
                {totalFiles > 0 ? `${files.length} of ${totalFiles} ${totalFiles === 1 ? translate("fileSingular") : translate("filePlural")}` : `0 ${translate("filePlural")}`}
              </p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {/* Sort and Filter Toggles */}
            <button
              onClick={() => setShowSorting(!showSorting)}
              className="btn-secondary flex items-center space-x-1 px-3 py-1 text-xs"
            >
              <ArrowUpDown className="h-4 w-4" />
              <span>{translate("sort")}</span>
            </button>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary flex items-center space-x-1 px-3 py-1 text-xs"
            >
              <Filter className="h-4 w-4" />
              <span>{translate("filters")}</span>
            </button>

            {/* Bulk Actions */}
            {selectedFiles.size > 0 && (
              <>
                <span className="text-sm text-[var(--color-text-secondary)]">
                  {translate("selected", {
                    count: selectedFiles.size,
                    plural: selectedFiles.size !== 1 ? "s" : "",
                  })}
                </span>
                {locationWritable && (
                  <button
                    onClick={handleBulkFileMove}
                    className="btn-secondary flex items-center space-x-1 px-3 py-1 text-sm"
                  >
                    <FolderInput className="h-4 w-4" />
                    <span>{translate("moveTo")}</span>
                  </button>
                )}
                <button
                  onClick={handleBulkDownload}
                  className="btn-primary flex items-center space-x-1 px-3 py-1 text-sm"
                >
                  <Download className="h-4 w-4" />
                  <span>{translate("downloadAll")}</span>
                </button>
                <button
                  onClick={handleBulkDeleteClick}
                  disabled={bulkDeleting}
                  className="btn-destructive flex items-center space-x-1 px-3 py-1 text-sm disabled:opacity-50"
                >
                  <Trash className="h-4 w-4" />
                  <span>{translate("deleteSelected")}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Sorting Controls */}
        {showSorting && (
          <div className="mt-4 p-4 rounded-xl border border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
            <div className="flex items-center space-x-4">
              <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
                {translate("sortBy")}:
              </span>
              <div className="flex items-center space-x-2">
                {[
                  { field: "name", label: translate("name") },
                  { field: "created_at", label: translate("createdAt") },
                  { field: "last_updated_at", label: translate("lastUpdatedAt") },
                  ...(isDateEnabledCollection ? [
                    { field: "start_date", label: translate("startDate") },
                    { field: "end_date", label: translate("endDate") },
                    { field: "status", label: translate("status") },
                  ] : []),
                ].map(({ field, label }) => {
                  const isActive = sortBy === field;
                  return (
                    <button
                      key={field}
                      onClick={() => handleSortChange(field)}
                      className="flex items-center space-x-1 px-3 py-1 text-xs rounded-lg border transition-colors font-medium"
                      style={{
                        backgroundColor: isActive ? "var(--color-mint)" : "transparent",
                        color: isActive ? "white" : "var(--color-text-secondary)",
                        borderColor: isActive ? "var(--color-mint)" : "var(--color-border)",
                      }}
                    >
                      <span>{label}</span>
                      {getSortIcon(field)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Filter Controls */}
        {showFilters && (
          <div className="mt-4 p-4 rounded-xl border border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* File Type Filter */}
              <div>
                <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                  {translate("fileType")}
                </label>
                <CustomSelect
                  value={fileTypeFilter}
                  onChange={(val) => setFileTypeFilter(val)}
                  options={[
                    { value: "all", label: translate("allTypes") },
                    { value: "pdf", label: "PDF" },
                    { value: "markdown", label: "Markdown" },
                    { value: "text", label: "Text" },
                  ]}
                />
              </div>

              {/* Campaign Status Filter */}
              {isDateEnabledCollection && (
                <div>
                  <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                    {translate("status")}
                  </label>
                  <CustomSelect
                    value={campaignStatusFilter}
                    onChange={(val) => setCampaignStatusFilter(val)}
                    options={[
                      { value: "all", label: translate("allStatuses") },
                      { value: "active", label: translate("campaignActive") },
                      { value: "pending", label: translate("campaignPending") },
                      { value: "expired", label: translate("campaignExpired") },
                    ]}
                  />
                </div>
              )}

              {/* Chat Filter - Only show if more than 1 active chat */}
              {availableChats &&
                Array.isArray(availableChats) &&
                availableChats.filter((chat) => chat && chat.is_active).length >
                  1 && (
                  <div>
                    <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                      {translate("chat")}
                    </label>
                    <CustomSelect
                      value={chatFilter}
                      onChange={(val) => setChatFilter(val)}
                      options={[
                        { value: "all", label: translate("allChats") },
                        ...availableChats
                          .filter((chat) => chat && chat.is_active)
                          .sort((a, b) => (a.chatwoot_inbox_id || 0) - (b.chatwoot_inbox_id || 0))
                          .map((chat) => ({ value: chat.id, label: chat.name })),
                      ]}
                    />
                  </div>
                )}

              {/* Created By Filter */}
              <div>
                <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                  {translate("createdBy")}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--color-text-muted)]" />
                  <input
                    type="text"
                    value={createdByFilter}
                    onChange={(e) => setCreatedByFilter(e.target.value)}
                    placeholder={translate("createdBy")}
                    className="field-input pl-10 pr-4 py-2 w-full"
                  />
                </div>
              </div>

              {/* Last Updated By Filter */}
              <div>
                <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                  {translate("lastUpdatedBy")}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--color-text-muted)]" />
                  <input
                    type="text"
                    value={lastUpdatedByFilter}
                    onChange={(e) => setLastUpdatedByFilter(e.target.value)}
                    placeholder={translate("lastUpdatedBy")}
                    className="field-input pl-10 pr-4 py-2 w-full"
                  />
                </div>
              </div>
            </div>

            {/* Date Filters Section */}
            <div className="mt-4">
              <button
                onClick={() => setShowDateFilters(!showDateFilters)}
                className="flex items-center space-x-2 text-xs font-semibold text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
              >
                <span>{translate("dateFilters")}</span>
                {showDateFilters ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>

              {showDateFilters && (
                <div className="mt-3 p-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Created At Filter */}
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                        {translate("createdAt")}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          value={createdAtFilter.startDate}
                          onChange={(e) =>
                            setCreatedAtFilter({
                              ...createdAtFilter,
                              startDate: e.target.value,
                            })
                          }
                          className="field-input px-3 py-2"
                          placeholder={translate("startDate")}
                        />
                        <input
                          type="date"
                          value={createdAtFilter.endDate}
                          onChange={(e) =>
                            setCreatedAtFilter({
                              ...createdAtFilter,
                              endDate: e.target.value,
                            })
                          }
                          className="field-input px-3 py-2"
                          placeholder={translate("endDate")}
                        />
                      </div>
                    </div>

                    {/* Last Updated At Filter */}
                    <div>
                      <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                        {translate("lastUpdatedAt")}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          value={lastUpdatedAtFilter.startDate}
                          onChange={(e) =>
                            setLastUpdatedAtFilter({
                              ...lastUpdatedAtFilter,
                              startDate: e.target.value,
                            })
                          }
                          className="field-input px-3 py-2"
                        />
                        <input
                          type="date"
                          value={lastUpdatedAtFilter.endDate}
                          onChange={(e) =>
                            setLastUpdatedAtFilter({
                              ...lastUpdatedAtFilter,
                              endDate: e.target.value,
                            })
                          }
                          className="field-input px-3 py-2"
                        />
                      </div>
                    </div>

                    {/* Campaign Start Date Filter */}
                    {isDateEnabledCollection && (
                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                          {translate("startDate")}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            value={campaignStartDateFilter.startDate}
                            onChange={(e) =>
                              setCampaignStartDateFilter({
                                ...campaignStartDateFilter,
                                startDate: e.target.value,
                              })
                            }
                            className="field-input px-3 py-2"
                          />
                          <input
                            type="date"
                            value={campaignStartDateFilter.endDate}
                            onChange={(e) =>
                              setCampaignStartDateFilter({
                                ...campaignStartDateFilter,
                                endDate: e.target.value,
                              })
                            }
                            className="field-input px-3 py-2"
                          />
                        </div>
                      </div>
                    )}

                    {/* Campaign End Date Filter */}
                    {isDateEnabledCollection && (
                      <div>
                        <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                          {translate("endDate")}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            value={campaignEndDateFilter.startDate}
                            onChange={(e) =>
                              setCampaignEndDateFilter({
                                ...campaignEndDateFilter,
                                startDate: e.target.value,
                              })
                            }
                            className="field-input px-3 py-2"
                          />
                          <input
                            type="date"
                            value={campaignEndDateFilter.endDate}
                            onChange={(e) =>
                              setCampaignEndDateFilter({
                                ...campaignEndDateFilter,
                                endDate: e.target.value,
                              })
                            }
                            className="field-input px-3 py-2"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Clear Filters Button */}
            {(nameFilter ||
              fileTypeFilter !== "all" ||
              createdAtFilter.startDate ||
              createdAtFilter.endDate ||
              createdByFilter ||
              lastUpdatedByFilter ||
              lastUpdatedAtFilter.startDate ||
              lastUpdatedAtFilter.endDate ||
              campaignStartDateFilter.startDate ||
              campaignStartDateFilter.endDate ||
              campaignEndDateFilter.startDate ||
              campaignEndDateFilter.endDate ||
              campaignStatusFilter !== "all" ||
              chatFilter !== "all") && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setNameFilter("");
                    setFileTypeFilter("all");
                    setCreatedAtFilter({ startDate: "", endDate: "" });
                    setCreatedByFilter("");
                    setLastUpdatedByFilter("");
                    setLastUpdatedAtFilter({ startDate: "", endDate: "" });
                    setCampaignStartDateFilter({ startDate: "", endDate: "" });
                    setCampaignEndDateFilter({ startDate: "", endDate: "" });
                    setCampaignStatusFilter("all");
                    setChatFilter("all");
                  }}
                  className="btn-secondary text-xs"
                >
                  {translate("clearAllFilters")}
                </button>
              </div>
            )}
          </div>
        )}

      </div>
      )}

      {/* Select All — between toolbar and file list */}
      {files.length > 0 && (
        <div className="px-6 py-2 border-b border-[var(--color-border)]">
          <label className="flex items-center space-x-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
            <input
              type="checkbox"
              checked={selectedFiles.size === files.length && files.length > 0}
              onChange={handleSelectAll}
              className="rounded border-[var(--color-border)]"
            />
            <span>
              {selectedFiles.size === files.length && files.length > 0
                ? translate("deselectAll")
                : translate("selectAll")}
            </span>
          </label>
        </div>
      )}


      <div
        className="divide-y divide-[var(--color-border)]"
      >
        {isLoading && !folders.length ? (
          <div className="animate-pulse">
            {[...Array(Math.min(PAGE_SIZE, 5))].map((_, i) => (
              <div key={i} className="px-6 py-4 flex items-center gap-4 border-b border-[var(--color-border)]">
                <div className="w-7 h-8 rounded bg-[var(--color-skeleton-light)] flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-48 rounded bg-[var(--color-skeleton-light)]" />
                  <div className="h-2.5 w-32 rounded bg-[var(--color-skeleton-light)]" />
                </div>
                <div className="h-3 w-16 rounded bg-[var(--color-skeleton-light)]" />
              </div>
            ))}
          </div>
        ) : !files || (files.length === 0 && folders.length === 0 && !creatingFolder) ? (
          <div className="p-12 text-center">
            <File className="mx-auto h-12 w-12 text-[var(--color-text-muted)] mb-4" />
            <h3 className="font-semibold text-base text-[var(--color-text-primary)] mb-2">
              {translate("noFilesOrFolders")}
            </h3>
            <p className="text-[var(--color-text-secondary)]">
              {locationWritable
                ? translate("uploadFilesOrCreateFolder")
                : translate("readOnlyLocationHint")}
            </p>
          </div>
        ) : (
          <>
            {/* File skeleton rows while loading */}
            {isLoading && files.length === 0 && (
              <div className="animate-pulse">
                {[...Array(Math.min(PAGE_SIZE, 5))].map((_, i) => (
                  <div key={i} className="px-6 py-4 flex items-center gap-4 border-b border-[var(--color-border)]">
                    <div className="w-7 h-8 rounded bg-[var(--color-skeleton-light)] flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 w-48 rounded bg-[var(--color-skeleton-light)]" />
                      <div className="h-2.5 w-32 rounded bg-[var(--color-skeleton-light)]" />
                    </div>
                    <div className="h-3 w-16 rounded bg-[var(--color-skeleton-light)]" />
                  </div>
                ))}
              </div>
            )}

            {/* No results when filters are active */}
            {!isLoading && files.length === 0 && hasActiveFilter && (
              <div className="p-12 text-center">
                <Search className="mx-auto h-10 w-10 mb-3" style={{ color: "var(--color-text-muted)" }} />
                <h3 className="font-semibold text-sm text-[var(--color-text-primary)] mb-1">
                  {translate("noResultsFound")}
                </h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {translate("noResultsDescription")}
                </p>
              </div>
            )}

            {/* File rows */}
            {files.map((file) => {
              const isFileMoving = movingFilePaths.has(file.path);
              return (
          <div
            key={file.path}
            className={`px-6 py-4 transition-colors cursor-pointer${isFileMoving ? " animate-pulse pointer-events-none" : ""}`}
            style={isFileMoving ? { opacity: 0.4 } : undefined}
            draggable={!isFileMoving}
            onClick={() => { if (!isFileMoving) handleViewFile(file); }}
            onDragStart={(e) => handleDragStart(e, "file", file.path)}
            onDragEnd={handleDragEnd}
            onContextMenu={(e) => {
              // Links and text fields keep their native menu (open-in-new-tab, paste); checkboxes don't.
              if (e.target.closest?.('a[href], textarea, input:not([type="checkbox"]):not([type="radio"])')) return;
              // Right-click inside our own open menu: keep it put, block the native one.
              if (menuRef.current && menuRef.current.contains(e.target)) {
                e.preventDefault();
                return;
              }
              // Match when the three-dot button is unavailable; the busy check is belt-and-braces
              // (the row is already pointer-events-none while busy).
              if (isFileMoving || deletingFile === file.path || bulkDeleting) return;
              e.preventDefault();
              triggerRef.current = cursorAnchor(e.clientX, e.clientY);
              setMenuPosition(computeMenuPlaceholder(triggerRef.current.getBoundingClientRect()));
              setMenuAnchorNonce((n) => n + 1);
              setFileMenuOpen(file.path);
              setFolderMenuOpen(null);
            }}
            onMouseEnter={(e) => { if (!isFileMoving) e.currentTarget.style.backgroundColor = "var(--color-mint-muted)"; }}
            onMouseLeave={(e) => { if (!isFileMoving) e.currentTarget.style.backgroundColor = ""; }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Checkbox for selection */}
                <input
                  type="checkbox"
                  checked={selectedFiles.has(file.path)}
                  onChange={() => handleSelectFile(file.path)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={deletingFile === file.path || bulkDeleting}
                  className="rounded border-[var(--color-border)] disabled:opacity-50"
                />

                <div className="flex-shrink-0">
                  {getFileIcon(file.file_type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p
                      className="text-sm font-medium text-[var(--color-text-primary)]"
                      title={file.name}
                    >
                      {truncateFileName(file.name)}
                    </p>
                    {isDateEnabledCollection &&
                      file.metadata &&
                      getStatusBadge(getCampaignStatus(file.metadata))}

                    {/* Folder path badge — shown in search mode */}
                    {hasActiveFilter && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (setNameFilter) setNameFilter("");
                          if (onNavigateToFolder) onNavigateToFolder(file.folder_id || null);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors hover:opacity-80"
                        style={{
                          backgroundColor: "var(--color-mint-muted)",
                          color: "var(--color-mint)",
                          border: "1px solid var(--color-mint-border)",
                        }}
                        title={file.folder_id ? (folderPathCache[file.folder_id] || "") : translate("rootFolder")}
                      >
                        <Folder size={10} />
                        <span className="truncate max-w-[200px]">
                          {file.folder_id ? (folderPathCache[file.folder_id] || "...") : translate("rootFolder")}
                        </span>
                      </button>
                    )}

                    {/* Info Button with Tooltip */}
                    <div className="relative">
                      <button
                        onMouseEnter={(e) => {
                          const rect = e.target.getBoundingClientRect();
                          setTooltipPosition({
                            top: rect.top - 10,
                            left: rect.left,
                          });
                          setShowTooltip(file.path);
                        }}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Campaign Dates - Keep as they are */}
                  <div className="mt-2">
                    {isDateEnabledCollection &&
                      file.metadata &&
                      (file.metadata.ai_agent_knowledge_start_date ||
                        file.metadata.ai_agent_knowledge_end_date) && (
                        <div className="flex items-center space-x-6 text-xs">
                          {file.metadata.ai_agent_knowledge_start_date && (
                            <div className="flex items-center space-x-2 text-[var(--color-text-secondary)]">
                              <Play className="h-3 w-3 text-[var(--color-text-muted)]" />
                              <span>
                                {translate("startDate")}:
                              </span>
                              <span className="text-[var(--color-text-primary)] font-bold">
                                {timestampToDate(
                                  file.metadata.ai_agent_knowledge_start_date
                                ).toLocaleString("pt-PT", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          )}

                          {file.metadata.ai_agent_knowledge_end_date && (
                            <div className="flex items-center space-x-2 text-[var(--color-text-secondary)]">
                              <Square className="h-3 w-3 text-[var(--color-text-muted)]" />
                              <span>
                                {translate("endDate")}:
                              </span>
                              <span className="text-[var(--color-text-primary)] font-bold">
                                {timestampToDate(
                                  file.metadata.ai_agent_knowledge_end_date
                                ).toLocaleString("pt-PT", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          )}

                          <FileSourceLinks file={file} />
                        </div>
                      )}

                    {/* Source link (shown when dates block isn't rendered).
                        FileSourceLinks renders nothing when the file has no
                        valid links, so no link check is needed here. */}
                    {!(
                      isDateEnabledCollection &&
                      file.metadata &&
                      (file.metadata.ai_agent_knowledge_start_date ||
                        file.metadata.ai_agent_knowledge_end_date)
                    ) && (
                      <div className="flex items-center space-x-6 text-xs">
                        <FileSourceLinks file={file} />
                      </div>
                    )}

                    {/* Chats with Access */}
                    {canManageChats(file) && (
                      <div className="mt-2">
                        {file.chat_ids && file.chat_ids.length > 0 ? (
                          <div className="flex items-center space-x-2 text-xs">
                            <Users className="h-3 w-3 text-[var(--color-text-muted)]" />
                            <span className="text-[var(--color-text-secondary)]"></span>
                            <div className="flex flex-wrap gap-1">
                              {file.chat_ids.map((chatId) => (
                                <span
                                  key={chatId}
                                  className="inline-flex items-center px-2 py-0.5 border border-[var(--color-border)] rounded text-xs text-[var(--color-text-secondary)] bg-[var(--color-surface)]"
                                >
                                  {getChatName(chatId)}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          // Sem chat nenhum é uma avaria NESTA lista: uma campanha da
                          // VTEX em fila nasce sem chat de propósito, e o backend deixou
                          // de a listar por isso mesmo (RPAA-229, o
                          // `hide_unassigned_created_by` do `list-files`). O que chega
                          // aqui sem chat é um ficheiro a que alguém se esqueceu de dar
                          // um. Noutros ecrãs a mesma ausência quer dizer outra coisa: o
                          // Vector Store conta os vectores sem chat, e aí as em fila
                          // entram.
                          <div className="flex items-center space-x-2 text-xs">
                            <Users className="h-3 w-3 text-[var(--color-error)]" />
                            <span className="text-[var(--color-error)]">
                              {translate("noChatsAssigned")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* File action menu (three-dot) */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (fileMenuOpen === file.path) {
                      setFileMenuOpen(null);
                    } else {
                      triggerRef.current = e.currentTarget;
                      setMenuPosition(computeMenuPlaceholder(e.currentTarget.getBoundingClientRect()));
                      setFileMenuOpen(file.path);
                    }
                  }}
                  disabled={deletingFile === file.path || bulkDeleting}
                  className="p-1.5 rounded-lg hover:bg-[var(--color-surface-raised)] transition-colors disabled:opacity-50"
                >
                  {deletingFile === file.path ? (
                    <div style={{ width: 16, height: 16, border: "2px solid var(--color-error)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  ) : (
                    <MoreVertical size={16} style={{ color: "var(--color-text-muted)" }} />
                  )}
                </button>
                {fileMenuOpen === file.path && (
                  <div
                    ref={menuRef}
                    className="fixed w-48 rounded-xl shadow-lg z-[9999] py-1"
                    style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", ...menuPosition }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => { handleViewFile(file); setFileMenuOpen(null); }}
                      className="w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-[var(--color-surface-raised)] transition-colors"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      <Eye size={14} /> {translate("viewFile")}
                    </button>
                    {locationWritable && (
                      <>
                        {(file.file_type === "text" || file.file_type === "markdown") && (
                          <button
                            onClick={() => { handleEditFile(file); setFileMenuOpen(null); }}
                            className="w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-[var(--color-surface-raised)] transition-colors"
                            style={{ color: "var(--color-text-secondary)" }}
                          >
                            <Edit size={14} /> {translate("editFile")}
                          </button>
                        )}
                        {isDateEnabledCollection && file.metadata && (
                          <button
                            onClick={() => { handleOpenDateModal(file); setFileMenuOpen(null); }}
                            className="w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-[var(--color-surface-raised)] transition-colors"
                            style={{ color: "var(--color-text-secondary)" }}
                          >
                            <Settings size={14} /> {translate("fileSettings")}
                          </button>
                        )}
                        {canManageChats(file) && (
                          <button
                            onClick={() => { handleOpenChatModal(file); setFileMenuOpen(null); }}
                            className="w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-[var(--color-surface-raised)] transition-colors"
                            style={{ color: "var(--color-text-secondary)" }}
                          >
                            <Users size={14} /> {translate("manageChats")}
                          </button>
                        )}
                        <button
                          onClick={() => { setShowMoveModal({ type: "file", id: file.path, name: file.name }); setFileMenuOpen(null); }}
                          className="w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-[var(--color-surface-raised)] transition-colors"
                          style={{ color: "var(--color-text-secondary)" }}
                        >
                          <FolderInput size={14} /> {translate("moveTo")}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => { handleDownloadFile(file); setFileMenuOpen(null); }}
                      className="w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-[var(--color-surface-raised)] transition-colors"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      <Download size={14} /> {translate("downloadFile")}
                    </button>
                    {locationWritable && (
                      <>
                        <div style={{ borderTop: "1px solid var(--color-border)", margin: "4px 0" }} />
                        <button
                          onClick={() => { handleDeleteClick(file.path); setFileMenuOpen(null); }}
                          className="w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 hover:bg-[var(--color-surface-raised)] transition-colors"
                          style={{ color: "var(--color-error)" }}
                        >
                          <Trash2 size={14} /> {translate("deleteFile")}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
              );
            })}
          </>
        )}

        {/* Infinite scroll sentinel + skeleton rows */}
        {hasMore && (
          <>
            {isLoadingMore && (
              <div className="animate-pulse">
                {[...Array(Math.min(PAGE_SIZE, Math.max(1, totalFiles - files.length)))].map((_, i) => (
                  <div key={`skel-${i}`} className="px-6 py-4 flex items-center gap-4 border-t border-[var(--color-border)]">
                    <div className="w-7 h-8 rounded bg-[var(--color-skeleton-light)] flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 w-48 rounded bg-[var(--color-skeleton-light)]" />
                      <div className="h-2.5 w-32 rounded bg-[var(--color-skeleton-light)]" />
                    </div>
                    <div className="h-3 w-16 rounded bg-[var(--color-skeleton-light)]" />
                  </div>
                ))}
              </div>
            )}
            <div ref={sentinelRef} className="h-1" />
          </>
        )}
      </div>

      {/* Folder Delete Confirmation Modal */}
      {showFolderDeleteConfirm && (() => {
        const fCount = folderDeleteCounts?.folders_count || 0;
        const fiCount = folderDeleteCounts?.files_count || 0;
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card rounded-xl border border-[var(--color-border)] p-6 max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(239,68,68,0.1)" }}>
                <Trash2 className="h-5 w-5 text-[var(--color-error)]" />
              </div>
              <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                {translate("deleteFolder")}
              </h3>
            </div>

            {/* Folder name */}
            <div
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-4"
              style={{ backgroundColor: "var(--color-surface-raised)", border: "1px solid var(--color-border)" }}
            >
              <Folder size={16} style={{ color: "var(--color-text-muted)" }} />
              <span className="text-sm font-medium text-[var(--color-text-primary)] truncate" title={showFolderDeleteConfirm.name}>
                {showFolderDeleteConfirm.name}
              </span>
            </div>

            {/* Contents summary */}
            {(fCount > 0 || fiCount > 0) && (
              <div className="flex gap-3 mb-4">
                {fCount > 0 && (
                  <div
                    className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.12)" }}
                  >
                    <Folder size={14} style={{ color: "var(--color-error)" }} />
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      <span className="font-semibold text-[var(--color-text-primary)]">{fCount}</span> {fCount === 1 ? translate("folderSingular") : translate("folderPlural")}
                    </span>
                  </div>
                )}
                {fiCount > 0 && (
                  <div
                    className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.12)" }}
                  >
                    <File size={14} style={{ color: "var(--color-error)" }} />
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      <span className="font-semibold text-[var(--color-text-primary)]">{fiCount}</span> {fiCount === 1 ? translate("fileSingular") : translate("filePlural")}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Warning */}
            <p className="text-sm text-[var(--color-text-secondary)] mb-5">
              {translate("deleteActionWarning")}
            </p>

            <div className="flex justify-end space-x-3">
              <button onClick={() => { setShowFolderDeleteConfirm(null); setFolderDeleteCounts(null); }} className="btn-secondary">
                {translate("cancel")}
              </button>
              <button onClick={() => handleDeleteFolder(showFolderDeleteConfirm.id)} className="btn-destructive">
                {translate("delete")}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Assign Role Modal (internal_procedures managers, top-level folders) */}
      {showAssignRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card rounded-xl border border-[var(--color-border)] p-6 max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                <Shield className="h-5 w-5" style={{ color: "var(--color-mint)" }} />
              </div>
              <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                {translate("assignRole")}
              </h3>
            </div>

            {/* Folder name */}
            <div
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-4"
              style={{ backgroundColor: "var(--color-surface-raised)", border: "1px solid var(--color-border)" }}
            >
              <Folder size={16} style={{ color: "var(--color-text-muted)" }} />
              <span className="text-sm font-medium text-[var(--color-text-primary)] truncate" title={showAssignRoleModal.name}>
                {showAssignRoleModal.name}
              </span>
            </div>

            {/* Role input */}
            <input
              type="text"
              value={assignRoleValue}
              onChange={(e) => setAssignRoleValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); handleAssignRole(); }
                if (e.key === "Escape") { setShowAssignRoleModal(null); setAssignRoleValue(""); }
              }}
              placeholder={translate("requiredRolePlaceholder")}
              className="field-input w-full mb-3"
              autoFocus
            />

            <p className="text-sm text-[var(--color-text-secondary)] mb-5">
              {translate("assignRoleDescription")}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowAssignRoleModal(null); setAssignRoleValue(""); }}
                className="btn-secondary"
                disabled={savingRole}
              >
                {translate("cancel")}
              </button>
              <button onClick={handleAssignRole} className="btn-primary" disabled={savingRole}>
                {savingRole ? translate("saving") || "Saving..." : translate("saveChanges") || "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Folder Delete Confirmation Modal */}
      {showBulkFolderDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div ref={bulkFolderDeleteModalRef} className="card rounded-xl border border-[var(--color-border)] p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(239,68,68,0.1)" }}>
                <Trash2 className="h-5 w-5 text-[var(--color-error)]" />
              </div>
              <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                {translate("deleteSelected")}
              </h3>
            </div>

            <p className="text-sm text-[var(--color-text-secondary)] mb-4">
              {translate("deleteMultipleFilesConfirmation", { count: selectedFolders.size, plural: selectedFolders.size !== 1 ? "s" : "" })}
            </p>

            {/* Contents summary */}
            {((bulkFolderDeleteCounts?.totalFolders || 0) > 0 || (bulkFolderDeleteCounts?.totalFiles || 0) > 0) && (
              <div className="flex gap-3 mb-4">
                {(bulkFolderDeleteCounts?.totalFolders || 0) > 0 && (
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.12)" }}>
                    <Folder size={14} style={{ color: "var(--color-error)" }} />
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      <span className="font-semibold text-[var(--color-text-primary)]">{bulkFolderDeleteCounts.totalFolders}</span> {bulkFolderDeleteCounts.totalFolders === 1 ? translate("folderSingular") : translate("folderPlural")}
                    </span>
                  </div>
                )}
                {(bulkFolderDeleteCounts?.totalFiles || 0) > 0 && (
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.12)" }}>
                    <File size={14} style={{ color: "var(--color-error)" }} />
                    <span className="text-sm text-[var(--color-text-secondary)]">
                      <span className="font-semibold text-[var(--color-text-primary)]">{bulkFolderDeleteCounts.totalFiles}</span> {bulkFolderDeleteCounts.totalFiles === 1 ? translate("fileSingular") : translate("filePlural")}
                    </span>
                  </div>
                )}
              </div>
            )}

            <p className="text-sm text-[var(--color-text-secondary)] mb-5">{translate("deleteActionWarning")}</p>

            <div className="flex justify-end space-x-3">
              <button onClick={() => { setShowBulkFolderDeleteConfirm(false); setBulkFolderDeleteCounts(null); }} className="btn-secondary">{translate("cancel")}</button>
              <button onClick={handleBulkFolderDeleteConfirm} className="btn-destructive">{translate("delete")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Move To Folder Modal */}
      {showMoveModal && (
        <MoveToFolderModal
          collectionName={collectionName}
          currentFolderId={folderId}
          excludeFolderId={showMoveModal.type === "folder" ? showMoveModal.id : (showMoveModal.type === "folders" ? showMoveModal.ids[0] : null)}
          onSelect={handleMoveToFolder}
          onClose={() => setShowMoveModal(null)}
          itemName={showMoveModal.name}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={bulkDeleteModalRef}
            className="card rounded-xl border border-[var(--color-border)] p-6 max-w-md w-full mx-4 max-h-[80vh] flex flex-col"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(239,68,68,0.1)" }}>
                  <Trash className="h-5 w-5 text-[var(--color-error)]" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                  {translate("deleteMultipleFiles")}
                </h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {translate("deleteMultipleFilesConfirmation", {
                    count: selectedFiles.size,
                    plural: selectedFiles.size > 1 ? "s" : "",
                  })}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                {translate("filesToBeDeleted")}
              </p>
              <div className="max-h-40 overflow-y-auto rounded-xl border border-[var(--color-border)] p-2 bg-[var(--color-surface)]">
                {Array.from(selectedFiles).map((filePath) => (
                  <div key={filePath} className="text-sm text-[var(--color-text-secondary)] py-1 truncate">
                    • {filePath.split("/").pop()}
                  </div>
                ))}
              </div>
            </div>

            {/* O aviso segue o que a selecção é. "Não pode ser desfeito" é falso numa
                campanha da VTEX, e numa selecção misturada as duas frases são precisas. */}
            {Array.from(selectedFiles).some((path) => !isSyncedCampaign(files.find((f) => f.path === path))) && (
              <p className="text-sm text-[var(--color-error)] mb-4">
                {translate("deleteActionWarning")}
              </p>
            )}
            {Array.from(selectedFiles).some((path) => isSyncedCampaign(files.find((f) => f.path === path))) && (
              <p className="text-sm text-[var(--color-text-secondary)] mb-4">
                {translate("deleteCampaignWarning")}
              </p>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleBulkDeleteCancel}
                disabled={bulkDeleting}
                className="btn-secondary disabled:opacity-50"
              >
                {translate("cancel")}
              </button>
              <button
                onClick={handleBulkDeleteConfirm}
                disabled={bulkDeleting}
                className="btn-destructive disabled:opacity-50"
              >
                {bulkDeleting ? (
                  <div className="flex items-center space-x-2">
                    <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <span>{translate("deleting")}</span>
                  </div>
                ) : (
                  translate("deleteFiles", {
                    count: selectedFiles.size,
                    plural: selectedFiles.size > 1 ? "s" : "",
                  })
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single File Delete Confirmation Modal */}
      {showSingleDeleteConfirm && fileToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={singleDeleteModalRef}
            className="card rounded-xl border border-[var(--color-border)] p-6 max-w-md w-full mx-4"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(239,68,68,0.1)" }}>
                  <Trash2 className="h-5 w-5 text-[var(--color-error)]" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                  {translate("deleteFile")}
                </h3>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {translate("deleteFileConfirmation", {
                    fileName: fileToDelete.name,
                  })}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                {translate("fileToBeDeleted")}:
              </p>
              <div className="rounded-xl border border-[var(--color-border)] p-2 bg-[var(--color-surface)]">
                <div className="text-sm text-[var(--color-text-secondary)] py-1">
                  • {fileToDelete.name}
                </div>
              </div>
            </div>

            <p
              className={`text-sm mb-4 ${
                isSyncedCampaign(fileToDelete)
                  ? "text-[var(--color-text-secondary)]"
                  : "text-[var(--color-error)]"
              }`}
            >
              {translate(isSyncedCampaign(fileToDelete) ? "deleteCampaignWarning" : "deleteActionWarning")}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deletingFile === fileToDelete.path}
                className="btn-secondary disabled:opacity-50"
              >
                {translate("cancel")}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deletingFile === fileToDelete.path}
                className="btn-destructive disabled:opacity-50"
              >
                {deletingFile === fileToDelete.path ? (
                  <div className="flex items-center space-x-2">
                    <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <span>{translate("deleting")}</span>
                  </div>
                ) : (
                  translate("delete")
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {showFileViewer && viewingFile && (
        <FileViewer
          file={viewingFile}
          isOpen={showFileViewer}
          onClose={handleCloseFileViewer}
          onFileUpdated={onFileUpdated}
          startInEditMode={startInEditMode}
          collectionName={collectionName}
          // A loja pela qual a lista está filtrada. Sem ela, apagar uma campanha aqui
          // dava 409 num tenant com duas lojas e não havia saída: a mensagem manda
          // escolher a loja no filtro, e o filtro estava atrás do visualizador.
          rejectingChatId={chatFilter !== "all" ? chatFilter : null}
          files={files}
          onNavigate={(file) => {
            setViewingFile(file);
            setStartInEditMode(false);
          }}
        />
      )}

      {/* Campaign Dates Edit Modal */}
      {showDateModal && editingFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={dateModalRef}
            // Capped height with a scrollable body: the links list is unbounded (up to 20 rows),
            // and without this the panel grew past the viewport and took Save/Cancel with it.
            // 3xl rather than lg because each link row is a URL plus a name side by side, and at
            // 14px a .../destaque/<hash> URL needs 432px of text: at lg the URL box gave 238px, so
            // links sharing a prefix (RP's destaque and campanha-coluna forms) all read
            // identically. 3xl leaves ~478px, which fits every URL form in use with headroom.
            className="card rounded-xl border border-[var(--color-border)] p-6 max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col"
          >
            <div className="shrink-0 flex items-center justify-between mb-4 border-b border-[var(--color-border)] pb-4">
              <div className="flex items-center">
                <Settings className="h-6 w-6 mr-2" style={{ color: "var(--color-mint)" }} />
                <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                  {translate("fileSettings")}
                </h3>
              </div>
              <button
                onClick={handleCloseDateModal}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="shrink-0 text-sm text-[var(--color-text-secondary)] mb-4">
              {translate("campaignDatesDescription", {
                count: 1,
                plural: "",
              })}
              : <span className="font-semibold text-[var(--color-text-primary)]">{editingFile.name}</span>
            </p>

            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 mb-6 -mx-1 px-1">
              <div>
                <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                  {translate("startDate")}
                </label>
                <DatePicker
                  selected={modalStartDate}
                  onChange={(date) => setModalStartDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText={translate("selectStartDate")}
                  className="field-input w-full"
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  {translate("startDateOptional")}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                  {translate("endDate")}
                </label>
                <DatePicker
                  selected={modalEndDate}
                  onChange={(date) => {
                    // Use ref to get the most recent value (avoids stale state in rapid onChange calls)
                    const currentEndDate =
                      modalEndDateRef.current || modalEndDate;
                    if (date) {
                      const adjustedDate = new Date(date);
                      const incomingTimeIsMidnight =
                        date.getHours() === 0 &&
                        date.getMinutes() === 0 &&
                        date.getSeconds() === 0;
                      const isFirstSelection = currentEndDate === null;
                      const currentTimeIsDefault =
                        currentEndDate &&
                        currentEndDate.getHours() === 23 &&
                        currentEndDate.getMinutes() === 59;
                      const currentTimeIsCustom =
                        currentEndDate &&
                        !(
                          currentEndDate.getHours() === 23 &&
                          currentEndDate.getMinutes() === 59
                        );

                      // If user has custom time and incoming is midnight, preserve the custom time
                      if (hasCustomTimeRef.current && incomingTimeIsMidnight) {
                        if (currentEndDate) {
                          adjustedDate.setHours(
                            currentEndDate.getHours(),
                            currentEndDate.getMinutes(),
                            currentEndDate.getSeconds(),
                            currentEndDate.getMilliseconds()
                          );
                        }
                      } else if (
                        currentTimeIsCustom &&
                        incomingTimeIsMidnight
                      ) {
                        // Date change but user has custom time - preserve it
                        adjustedDate.setHours(
                          currentEndDate.getHours(),
                          currentEndDate.getMinutes(),
                          currentEndDate.getSeconds(),
                          currentEndDate.getMilliseconds()
                        );
                        hasCustomTimeRef.current = true;
                      } else if (!incomingTimeIsMidnight) {
                        // User is selecting a non-midnight time - mark as custom
                        hasCustomTimeRef.current = true;
                      } else if (
                        isFirstSelection ||
                        (incomingTimeIsMidnight && currentTimeIsDefault)
                      ) {
                        // First selection or date-only change - use default 23:59
                        adjustedDate.setHours(23, 59, 59, 999);
                        hasCustomTimeRef.current = false;
                      }
                      setModalEndDate(adjustedDate);
                      modalEndDateRef.current = adjustedDate; // Update ref immediately
                    } else {
                      setModalEndDate(null);
                      modalEndDateRef.current = null;
                      hasCustomTimeRef.current = false;
                    }
                  }}
                  onTimeChange={(time) => {
                    const currentEndDate =
                      modalEndDateRef.current || modalEndDate;
                    if (time && currentEndDate) {
                      const newDate = new Date(currentEndDate);
                      newDate.setHours(
                        time.getHours(),
                        time.getMinutes(),
                        time.getSeconds(),
                        time.getMilliseconds()
                      );
                      hasCustomTimeRef.current = true;
                      setModalEndDate(newDate);
                      modalEndDateRef.current = newDate; // Update ref immediately
                    }
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText={translate("selectEndDate")}
                  className="field-input w-full"
                  minDate={modalStartDate}
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  {translate("noEndDate")}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
                  {translate("sourceUrlsLabel")}
                </label>
                <SourceLinksField
                  value={modalSourceUrls}
                  onChange={setModalSourceUrls}
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  {translate("sourceUrlsHelper")}
                </p>
              </div>
            </div>

            <div className="shrink-0 flex justify-end space-x-3">
              <button
                onClick={handleCloseDateModal}
                disabled={savingDates}
                className="btn-secondary disabled:opacity-50"
              >
                {translate("cancel")}
              </button>
              <button
                onClick={handleSaveCampaignDates}
                disabled={savingDates}
                className="btn-primary disabled:opacity-50"
              >
                {savingDates ? (
                  <div className="flex items-center space-x-2">
                    <div style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <span>{translate("saving")}</span>
                  </div>
                ) : (
                  translate("save")
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Tooltip */}
      {showTooltip && (() => {
        const isFolder = showTooltip.startsWith("folder::");
        let createdBy, createdAt, lastUpdatedBy, lastUpdatedAt;

        if (isFolder) {
          const fId = showTooltip.replace("folder::", "");
          const folder = folders.find((f) => f.id === fId);
          createdBy = folder?.created_by;
          createdAt = folder?.created_at;
          lastUpdatedBy = folder?.last_updated_by;
          lastUpdatedAt = folder?.last_updated_at;
        } else {
          const file = files.find((f) => f.path === showTooltip);
          createdBy = file?.metadata?.created_by || file?.created_by;
          createdAt = file?.metadata?.created_at || file?.created_at || file?.modified;
          lastUpdatedBy = file?.last_updated_by;
          lastUpdatedAt = file?.last_updated_at;
        }

        const formatTooltipDate = (ts) => {
          if (!ts) return "N/A";
          // Folder timestamps are ISO strings, file timestamps are unix
          const d = typeof ts === "string" ? new Date(ts) : new Date(ts * 1000);
          const locale = translate("createdBy") === "Criado por" ? "pt-PT" : "en-GB";
          return d.toLocaleString(locale, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
        };

        return (
          <div
            className="fixed px-4 py-3 text-sm rounded-xl z-[999999] max-w-[500px] border border-[var(--color-border)] shadow-md"
            style={{
              backgroundColor: "var(--color-surface)",
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translateY(-100%)",
            }}
          >
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <Calendar className="h-4 w-4 text-[var(--color-text-muted)] flex-shrink-0" />
                <div className="text-[var(--color-text-secondary)] text-sm whitespace-nowrap">
                  <span className="font-semibold text-[var(--color-text-muted)]">
                    {translate("createdBy")}
                  </span>{" "}
                  <span className="font-semibold text-[var(--color-text-primary)]">
                    {createdBy || "N/A"}
                  </span>{" "}
                  <span className="font-semibold text-[var(--color-text-muted)]">
                    {translate("at")}
                  </span>{" "}
                  <span className="font-semibold text-[var(--color-text-primary)]">
                    {formatTooltipDate(createdAt)}
                  </span>
                </div>
              </div>

              {lastUpdatedAt && (lastUpdatedBy !== createdBy || lastUpdatedAt !== createdAt) && (
                <div className="flex items-center space-x-3">
                  <Clock className="h-4 w-4 text-[var(--color-text-muted)] flex-shrink-0" />
                  <div className="text-[var(--color-text-secondary)] text-sm whitespace-nowrap">
                    <span className="font-semibold text-[var(--color-text-muted)]">
                      {translate("lastUpdatedBy")}
                    </span>{" "}
                    <span className="font-semibold text-[var(--color-text-primary)]">
                      {lastUpdatedBy || "N/A"}
                    </span>{" "}
                    <span className="font-semibold text-[var(--color-text-muted)]">
                      {translate("at")}
                    </span>{" "}
                    <span className="font-semibold text-[var(--color-text-primary)]">
                      {formatTooltipDate(lastUpdatedAt)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[var(--color-border)]"></div>
          </div>
        );
      })()}

      {/* Chat Management Modal */}
      {showChatModal && editingFileChats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={chatModalRef}
            className="card rounded-xl border border-[var(--color-border)] p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4 border-b border-[var(--color-border)] pb-4">
              <div className="flex items-center">
                <Users className="h-6 w-6 mr-2" style={{ color: "var(--color-mint)" }} />
                <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                  {translate("manageChatsForFile")}
                </h3>
              </div>
              <button
                onClick={handleCloseChatModal}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                <span className="font-semibold text-[var(--color-text-primary)]">{translate("file")}:</span> {editingFileChats.name}
              </p>
              <p className="text-sm text-[var(--color-text-secondary)]">
                <span className="font-semibold text-[var(--color-text-primary)]">{translate("collection")}:</span> {collectionName}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-3">
                {translate("selectChats")}
              </label>

              <div className="space-y-2 max-h-64 overflow-y-auto border border-[var(--color-border)] rounded-xl p-3 bg-[var(--color-surface)]">
                {availableChats.length === 0 ? (
                  <p className="text-sm text-[var(--color-text-muted)]">
                    {translate("noChatsAvailable")}
                  </p>
                ) : (
                  availableChats
                    .filter((chat) => {
                      // Filter by active status
                      if (!chat.is_active) return false;
                      // For internal_procedures collection, only show internal chats
                      if (collectionName === "internal_procedures") {
                        return chat.internal_chat === true;
                      }
                      return true;
                    })
                    .sort(
                      (a, b) =>
                        (a.chatwoot_inbox_id || 0) - (b.chatwoot_inbox_id || 0)
                    )
                    .map((chat) => (
                      <label
                        key={chat.id}
                        className="flex items-center p-2 rounded-xl cursor-pointer hover:bg-[var(--color-surface-raised)]"
                      >
                        <input
                          type="checkbox"
                          checked={selectedFileChats.includes(chat.id)}
                          onChange={() => toggleChatSelection(chat.id)}
                          className="h-4 w-4 border-[var(--color-border)] rounded"
                        />
                        <span className="ml-3 text-sm text-[var(--color-text-primary)]">
                          {chat.name}
                        </span>
                      </label>
                    ))
                )}
              </div>

              <p
                className={`mt-2 text-xs ${
                  selectedFileChats.length === 0
                    ? "text-[var(--color-error)]"
                    : "text-[var(--color-text-muted)]"
                }`}
              >
                {selectedFileChats.length === 0
                  ? translate("atLeastOneChatRequired")
                  : translate("chatsSelected", {
                      count: selectedFileChats.length,
                    })}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseChatModal}
                disabled={savingChats}
                className="btn-secondary disabled:opacity-50"
              >
                {translate("cancel")}
              </button>
              <button
                onClick={handleSaveFileChats}
                disabled={savingChats}
                className="btn-primary disabled:opacity-50"
              >
                {savingChats ? translate("updating") : translate("updateChats")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileList;
